import Card from "@/components/Card";
import Combobox from "@/components/Combobox";
import Modal from "@/components/Modal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { web } from "@/config/System";
import { listSizeOptions } from "@/constants";
import useApiBaseUrl from "@/hooks/useApiBaseUrl";
import usePaymentMethod from "@/hooks/usePaymentMethod";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import {
  formatDate,
  formatDateIndonesia,
  formatRupiah,
  getInitials,
} from "@/lib/utils";
import {
  getDataSiswaLengkap,
  getDataStatusIsiUKTPenerimaKIPK,
  getListBerkasInvalid,
  getStatusPembayaranBriva,
} from "@/services/getData";
import {
  DataBerkas,
  DataPendaftaran,
  // DataPilihanProdi,
  DataSiswa,
  DataTagihanUktPlaza,
  LokasiKuliah,
  PaymentMethod,
  SysBerkas,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDownToLine,
  ArrowRight,
  Award,
  Banknote,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  FileDown,
  FileText,
  FileWarning,
  FileX2,
  Info,
  ListChecks,
  Loader2,
  MessageCircle,
  PartyPopper,
  Printer,
  Timer,
  Upload,
  UploadCloud,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { IoWarning } from "react-icons/io5";
import { LuCheck } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import DataLoaPasca from "./partial/DataLoaPasca";
import FormBerkas from "./partial/FormBerkas";
import FormTitipan from "./partial/FormTitipan";
import SkeletonPengumuman from "./partial/SkeletonPengumuman";
import { MdInfo } from "react-icons/md";

const getDataStatusGenerateKuitansi = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/daftar-kembali/status-generate-kuitansi?id_sys_periode=${id_sys_periode}`,
  );
  return response.data;
};

const getDataStatusGenerateAkun = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/daftar-kembali/status-generate-akun?id_sys_periode=${id_sys_periode}`,
  );
  return response.data;
};

const generateTagihanUkt = async (formData: FormData) => {
  const response = await api.post(
    `/siswa/daftar-kembali/generate-tagihan-ukt`,
    formData,
  );
  return response.data;
};

const getTagihanUkt = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/daftar-kembali/tagihan-ukt?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data?.data;
};

const getDataTagihanUktAktif = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/daftar-kembali/cek-tagihan-ukt-aktif?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data;
};

const konfirmasiKIPK = async (id_data_ukt_pendaftar_lulus: string) => {
  const response = await api.patch(`/siswa/daftar-kembali/konfirmasi-kipk`, {
    id_data_ukt_pendaftar_lulus,
  });
  return response.data;
};

const uploadUlangSuratPernyataanUkt = async (formData: FormData) => {
  const response = await api.post(
    `/siswa/daftar-kembali/upload-ulang-surat-pernyataan-ukt`,
    formData,
  );
  return response.data;
};

export default function PengumumanPage() {
  const { t } = useTranslation();

  const { user } = useUser();
  const { apiBaseUrl } = useApiBaseUrl();
  const navigate = useNavigate();
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const [openSize, setOpenSize] = useState(false);
  const [form, setForm] = useState({
    ukuran_baju: "",
    pilihan_lokasi_kuliah: "singaraja",
  });
  const queryClient = useQueryClient();

  const { listPaymentMethodPlaza, isPendingListPaymentMethodPlaza } =
    usePaymentMethod();

  const { data: dataSiswa, isPending: isPendingDataSiswa } = useQuery<
    DataSiswa & {
      fakultas_nama?: string;
      fakultas_name?: string;
      jurusan_nama?: string;
      jurusan_name?: string;
      prodi_nama?: string;
      prodi_name?: string;
    }
  >({
    queryKey: ["dataSiswaPengumuman", user?.id, sessionPeriode?.id_sys_periode],
    queryFn: () => getDataSiswaLengkap(sessionPeriode?.id_sys_periode || ""),
    enabled: !!user && !!sessionPeriode?.id_sys_periode,
  });

  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  const {
    dataPeriode,
    dataPeriodeActive,
    timePassed,
    isPendingPeriode,
    isFetched: isFetchedPeriode,
    timePassedActive,
    dataPeriodeCustom,
  } = useTimePeriode();

  const isJalurPascasarjana =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug === "pascasarjana";
  const isJalurInternasional =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug === "internasional";
  const isJalurProfesi =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug === "profesi";
  // const isJalurMandiri =
  //   dataPeriode?.jalur_jenis?.jalur?.nama_jalur.startsWith("Mandiri");

  const { data: dataBerkasInvalid, isPending: isPendingDataBerkasInvalid } =
    useQuery({
      queryKey: ["dataBerkasInvalid"],
      queryFn: () => getListBerkasInvalid(sessionPeriode?.id_sys_periode || ""),
      enabled: dataPeriode && !isJalurPascasarjana && !isJalurInternasional,
    });

  const { berkasSelesaiSanggah, totalBerkasSanggah, progressSanggah } =
    useMemo(() => {
      // Safety check: Jika data belum ter-fetch, masih pending, atau bukan array
      if (!dataBerkasInvalid || !Array.isArray(dataBerkasInvalid)) {
        return {
          berkasSelesaiSanggah: 0,
          totalBerkasSanggah: 0,
          progressSanggah: 0,
        };
      }

      const selesai = dataBerkasInvalid.filter(
        (sysBerkas: SysBerkas) =>
          sysBerkas?.data_berkas?.path_sanggah_berkas !== null,
      ).length;

      const total = dataBerkasInvalid.length;

      // Hitung persentase progress (0 - 100)
      const progress = total === 0 ? 0 : Math.round((selesai / total) * 100);

      return {
        berkasSelesaiSanggah: selesai,
        totalBerkasSanggah: total,
        progressSanggah: progress,
      };
    }, [dataBerkasInvalid]);

  const { data: listTagihanUkt, isFetched: isFetchedListTagihanUkt } = useQuery(
    {
      queryKey: ["listTagihanUkt"],
      queryFn: () =>
        getTagihanUkt(String(dataPendaftaran?.id_data_pendaftaran) || ""),
      enabled: !!dataPendaftaran,
      retry: false,
    },
  );

  // Dapatkan waktu saat ini
  // const now = new Date();

  // Cek apakah ada minimal 1 tagihan yang expired_date-nya lebih besar dari sekarang
  // const IS_TAGIHAN_AKTIF =
  //   listTagihanUkt?.some((tagihan: DataTagihanUktPlaza) => {
  //     if (!tagihan.expired_date) return false;

  //     // Konversi string "YYYY-MM-DD HH:mm:ss" ke format Date
  //     // (Mengganti spasi dengan 'T' untuk memastikan kompatibilitas di semua browser)
  //     const expiredDate = new Date(tagihan.expired_date.replace(" ", "T"));

  //     // Bandingkan dengan waktu saat ini
  //     return expiredDate > now;
  //   }) || false; // Fallback ke false jika listTagihanUkt bernilai null/undefined

  const {
    data: dataStatusPembayaran,
    isPending: isPendingDataStatusPembayaran,
  } = useQuery({
    queryKey: ["dataStatusPembayaran"],
    queryFn: () => getStatusPembayaranBriva(sessionPeriode?.id_sys_periode),
    enabled: listTagihanUkt && listTagihanUkt?.length > 0,
    retry: false,
  });

  useEffect(() => {
    // Periksa jika dataStatusPembayaran sudah ada dan tidak dalam keadaan isPending
    if (!isPendingDataStatusPembayaran && dataStatusPembayaran) {
      // Invalidate query lain di sini
      queryClient.invalidateQueries({ queryKey: ["dataSiswaPengumuman"] });
    }
  }, [dataStatusPembayaran, isPendingDataStatusPembayaran, queryClient]);

  const {
    data: dataStatusIsiUKTpenerimaKIPK,
    isPending: isPendingDataStatusIsiUKTpenerimaKIPK,
    isFetched: isFetchedDataStatusIsiUKTpenerimaKIPK,
  } = useQuery({
    queryKey: ["dataStatusIsiUKTpenerimaKIPK", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getDataStatusIsiUKTPenerimaKIPK(sessionPeriode?.id_sys_periode || ""),
    enabled: !!sessionPeriode?.id_sys_periode,
  });

  const [isCopied, setIsCopied] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [showPdfSanggahBerkas, setShowPdfSanggahBerkas] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showAlertKuitansi, setShowAlertKuitansi] = useState(false);
  const [showConfirmKIPK, setShowConfirmKIPK] = useState(false);
  const [showSuratPernyataanUKT, setShowSuratPernyataanUKT] = useState(false);
  const [showUploadUlangSurat, setShowUploadUlangSurat] = useState(false);

  const [selectedBerkas, setSelectedBerkas] = useState<DataBerkas | null>(null);
  const [selecedSysBerkas, setSelectedSysBerkas] = useState<SysBerkas | null>(
    null,
  );
  const [showGenerateTagihan, setShowGenerateTagihan] = useState(false);
  const [showTagihan, setShowTagihan] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null);
  // const [selectedTagihan, setSelectedTagihan] =
  //   useState<DataTagihanUktPlaza | null>(
  //     listTagihanUkt && listTagihanUkt.length > 0
  //       ? listTagihanUkt[listTagihanUkt.length - 1]
  //       : null,
  //   );

  const [fileSuratPernyataanUKT, setFileSuratPernyataanUKT] =
    useState<File | null>(null);

  const { data: dataTagihanUktAktif, isPending: isPendingDataTagihanUkt } =
    useQuery({
      queryKey: ["statusTagihanAktif"],
      queryFn: () =>
        getDataTagihanUktAktif(
          String(dataPendaftaran?.id_data_pendaftaran) || "",
        ),
      enabled: !!showGenerateTagihan,
      retry: false,
    });
  const BISA_GENERATE_TAGIHAN = dataTagihanUktAktif?.status == true;

  // const sekarang = new Date();
  // const waktuMulaiBayar = dataPeriode?.waktu_mulai_bayar_ukt
  //   ? new Date(dataPeriode.waktu_mulai_bayar_ukt)
  //   : null;
  // const sudahMulaiBayar = waktuMulaiBayar && sekarang >= waktuMulaiBayagitr;
  const sudahMulaiBayar = true;
  const isSelesaiWaktuSanggahBerkas = (() => {
    if (!dataPeriode?.waktu_selesai_sanggah_berkas_daftarkembali) {
      return false;
    }

    const now = new Date();
    const waktuSelesaiDefault = new Date(
      dataPeriode.waktu_selesai_sanggah_berkas_daftarkembali,
    );

    if (now > waktuSelesaiDefault) {
      if (
        !dataPeriodeCustom?.custom_waktu_selesai_sanggah_berkas_daftarkembali
      ) {
        return true;
      }

      const waktuSelesaiCustom = new Date(
        dataPeriodeCustom.custom_waktu_selesai_sanggah_berkas_daftarkembali,
      );
      return now > waktuSelesaiCustom;
    }

    return false;
  })();

  const {
    mutate: mutateGenerateKuitansi,
    isPending: isPendingGenerateKuitansi,
    error: errorGenerateKuitansi,
  } = useMutation({
    mutationFn: () =>
      getDataStatusGenerateKuitansi(sessionPeriode?.id_sys_periode || ""),
    onSuccess: (data) => {
      if (data?.ok) {
        window.open(
          `${apiBaseUrl}/siswa/daftar-kembali/generate-kuitansi-briva?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
        );
        toast("Memproses cetak tagihan.");
      } else {
        toast(data?.message);
      }
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
      setShowAlertKuitansi(true);
    },
  });

  const { mutate, isPending, error } = useMutation({
    mutationFn: () =>
      getDataStatusGenerateAkun(sessionPeriode?.id_sys_periode || ""),
    onSuccess: (data) => {
      if (data?.ok) {
        window.open(
          `${apiBaseUrl}/siswa/daftar-kembali/generate-akun-eganesha?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
          "_blank",
        );
        toast("Memproses cetak nim.");
      } else {
        toast(data?.message);
      }
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
      setShowAlert(true);
    },
  });

  const {
    mutate: mutateGenerateTagihan,
    isPending: isPendingGenerateTagihan,
    error: errorGenerateTagihan,
  } = useMutation({
    mutationFn: generateTagihanUkt,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listTagihanUkt"] });
      queryClient.invalidateQueries({ queryKey: ["dataSiswaPengumuman"] });
      toast.success(data?.message);
      setShowGenerateTagihan(false);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const serverMessage =
        err.response?.data?.message || "Terjadi kesalahan sistem.";

      toast.error(`${serverMessage}. Silakan coba gunakan bank lain.`);
    },
  });

  const { mutate: mutateKonfirmasiKIPK, isPending: isPendingKonfirmasiKIPK } =
    useMutation({
      mutationFn: konfirmasiKIPK,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["dataSiswaPengumuman"] });
        toast.success(data?.message);
        setShowConfirmKIPK(false);
      },
      onError: (
        error: AxiosError<{
          message?: string;
          errors?: Record<string, string[]>;
        }>,
      ) => {
        const err = error as AxiosError<{
          message?: string;
          errors?: Record<string, string[]>;
        }>;
        const errorMessage =
          err.response?.data?.message || "An unexpected error occurred.";
        toast.error(errorMessage);
      },
    });

  const {
    mutate: mutateUploadUlangSurat,
    isPending: isPendingUploadUlangSurat,
    error: errorUploadUlangSurat,
  } = useMutation({
    mutationFn: uploadUlangSuratPernyataanUkt,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listTagihanUkt"] });
      queryClient.invalidateQueries({ queryKey: ["dataSiswaPengumuman"] });
      toast.success(data?.message);
      setShowUploadUlangSurat(false);
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    },
  });

  const handleGenerateAkun = () => {
    mutate();
  };

  const handleGenerateTagihanUkt = () => {
    if (!dataSiswa?.ukuran_baju && !form.ukuran_baju) {
      toast.error("Ukuran baju belum diisi");
      return;
    }

    if (!prodiLulus?.pilihan_lokasi_kuliah && !form.pilihan_lokasi_kuliah) {
      toast.error("Pilihan lokasi kuliah belum diisi");
      return;
    }

    const formData = new FormData();

    if (fileSuratPernyataanUKT)
      formData.append("file_surat_pernyataan_ukt", fileSuratPernyataanUKT);
    if (sessionPeriode?.id_sys_periode) {
      formData.append("id_sys_periode", sessionPeriode?.id_sys_periode);
    }
    if (dataPendaftaran?.id_data_pendaftaran) {
      formData.append(
        "id_data_pendaftaran",
        String(dataPendaftaran?.id_data_pendaftaran),
      );
    }
    if (selectedPaymentMethod?.id) {
      formData.append("payment_gateway_id", String(selectedPaymentMethod?.id));
    }
    if (form.ukuran_baju) {
      formData.append("ukuran_baju", form.ukuran_baju);
      formData.append("pilihan_lokasi_kuliah", form.pilihan_lokasi_kuliah);
    }

    mutateGenerateTagihan(formData);
  };

  const handleUploadUlangSuratPernyataanUkt = () => {
    const formData = new FormData();

    if (fileSuratPernyataanUKT)
      formData.append("file_surat_pernyataan_ukt", fileSuratPernyataanUKT);
    if (dataPendaftaran?.id_data_pendaftaran) {
      formData.append(
        "id_data_pendaftaran",
        String(dataPendaftaran?.id_data_pendaftaran) || "",
      );
    }

    mutateUploadUlangSurat(formData);
  };

  const handleKonfirmasiKIPK = () => {
    mutateKonfirmasiKIPK(
      dataPendaftaran?.data_penetapan_ukt?.id_data_ukt_pendaftar_lulus || "",
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; // Menggunakan optional chaining agar lebih clean
    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB dalam Bytes

    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("Ukuran file terlalu besar. Maksimal adalah 1MB.");

        // Reset input agar user bisa memilih file yang sama setelah memperbaiki ukurannya
        e.target.value = "";
        return;
      }

      // Jika lolos validasi, simpan ke state
      setFileSuratPernyataanUKT(file);
      toast.success("File berhasil dipilih.");
    }
  };

  // useEffect(() => {
  //   if (!listTagihanUkt) return;
  //   setSelectedTagihan(listTagihanUkt[listTagihanUkt.length - 1]);
  // }, [listTagihanUkt]);

  useEffect(() => {
    if (timePassedActive?.waktu_pengumuman) return;
    if (isFetchedPeriode && timePassed.waktu_pengumuman === false)
      navigate("/dashboard");
  }, [isFetchedPeriode, timePassed]);

  useEffect(() => {
    if (
      isFetchedDataStatusIsiUKTpenerimaKIPK &&
      dataStatusIsiUKTpenerimaKIPK &&
      dataStatusIsiUKTpenerimaKIPK?.is_penerima_kipk &&
      dataStatusIsiUKTpenerimaKIPK?.is_valid_isi_ukt == 0 &&
      dataStatusIsiUKTpenerimaKIPK?.periode_custom?.custom_waktu_selesai_isi_ukt
    ) {
      if (
        dataPeriodeCustom?.custom_waktu_pengumuman_ukt &&
        new Date(dataPeriodeCustom.custom_waktu_pengumuman_ukt) <= new Date()
      ) {
        return;
      } else {
        navigate("/daftar-kembali");
      }
    } else if (
      dataPeriodeCustom?.custom_waktu_pengumuman_ukt &&
      new Date(dataPeriodeCustom.custom_waktu_pengumuman_ukt) <= new Date()
    ) {
      return;
    } else if (!dataPeriodeCustom?.custom_waktu_pengumuman_ukt) {
      return;
    } else {
      navigate("/daftar-kembali");
    }
  }, [isFetchedDataStatusIsiUKTpenerimaKIPK, dataPeriodeCustom]);

  const isPenerimaBeasiswaPenuh =
    dataStatusPembayaran?.status_pembayaran == "FREE";
  const isSudahBayar =
    dataStatusPembayaran?.status_pembayaran == "SUDAH_BAYAR" ||
    (dataStatusPembayaran?.status_pembayaran == "FREE" && isJalurPascasarjana);
  const isPenerimaKIPK =
    dataPendaftaran?.data_penetapan_ukt?.kode_jenis_ukt == "32";

  const isAfirmasi =
    dataPendaftaran?.data_penetapan_ukt?.kode_jenis_ukt == "38";

  const isJalurNasional =
    dataPeriode?.jalur_jenis?.jalur?.nama_jalur?.startsWith("Nasional");
  const isJalurCbt = dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "cbt";
  const isJalurTalent =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "talentscouting";
  const isJalurPrestasi =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "prestasi";
  const isJalurRpl =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "prestasi";
  const isJalurKepemimpinan =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "kepemimpinan";
  const isJalurSekolahlab =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "sekolahlab";
  const isJalurSkorutbk =
    dataPeriode?.jalur_jenis?.nama_jalur_jenis_slug == "skorutbk";
  const isJalurMandiri =
    isJalurCbt ||
    isJalurTalent ||
    isJalurPrestasi ||
    isJalurRpl ||
    isJalurKepemimpinan ||
    isJalurSekolahlab ||
    isJalurSkorutbk;
  const isWajibSanggahBerkas =
    dataBerkasInvalid && dataBerkasInvalid?.length > 0;
  // const isWajibSanggahBerkas = true;
  const isSudahKonfirmasiKIPK =
    dataPendaftaran?.data_penetapan_ukt?.is_konfirmasi_kipk == 1;

  // 1. Cari apakah ada prodi yang lulus
  const prodiLulus = dataPendaftaran?.data_pilihan_prodi?.find(
    (prodi) => prodi.is_lulus,
  );

  // 2. Pastikan prodi lulus DITEMUKAN, dan lokasi kuliahnya masih KOSONG
  const butuhPilihLokasi = prodiLulus && !prodiLulus.pilihan_lokasi_kuliah;

  // 3. PERBAIKAN: Ambil array kelas_kuliah dari dalam 'prodiLulus', bukan 'dataPendaftaran'
  const pilihanKelas = prodiLulus?.unitkerja?.kelas_kuliah || [];

  // 4. Syarat akhir untuk menampilkan form:
  const tampilkanFormLokasi = butuhPilihLokasi && pilihanKelas.length > 1;

  const StatusBadge = ({
    status,
    isAfirmasi,
    isPenerimaKIPK,
  }: {
    status: number | boolean;
    isAfirmasi: boolean;
    isPenerimaKIPK: boolean;
  }) => {
    if (status == 2) {
      return (
        <Badge className="bg-green-600 hover:bg-green-700 gap-1 pl-1 pr-2">
          <CheckCircle2 className="w-3 h-3" /> {t("ukt.payment.status-2")}
        </Badge>
      );
    }
    if (status == 3)
      return <Badge variant="destructive">{t("ukt.payment.status-3")}</Badge>;
    if (status == 4)
      return <Badge variant="secondary">{t("ukt.payment.status-4")}</Badge>;
    if (status == 5) {
      return (
        <Badge className="bg-blue-600 hover:bg-blue-700">
          {isAfirmasi
            ? "Afirmasi (Lunas)"
            : isPenerimaKIPK
              ? "KIP-K (Lunas)"
              : "Beasiswa (Lunas)"}
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="border-amber-500 text-amber-600 gap-1 pl-1 pr-2 bg-amber-50"
      >
        <Clock className="w-3 h-3" /> {t("ukt.payment.status-1")}
      </Badge>
    );
  };

  if (
    isPendingDataBerkasInvalid ||
    isPendingDataStatusPembayaran ||
    isPendingDataStatusIsiUKTpenerimaKIPK
  ) {
    return <SkeletonPengumuman />;
  }

  if (
    dataPendaftaran?.data_penetapan_ukt?.is_titipan &&
    dataPendaftaran?.data_penetapan_ukt?.is_upload_ulang_surat_pernyataan == 0
  ) {
    return (
      <FormTitipan
        id_data_ukt_pendaftar_lulus={
          dataPendaftaran?.data_penetapan_ukt?.id_data_ukt_pendaftar_lulus
        }
        dataPenetapanUkt={dataPendaftaran?.data_penetapan_ukt}
      />
    );
  }

  if (
    dataPendaftaran &&
    !dataPendaftaran?.is_lulus &&
    !isJalurNasional &&
    !isJalurInternasional
  ) {
    return (
      <Card className="mb-5 border-l-4 border-l-red-500 shadow-sm dark:border-l-red-500">
        <CardHeader className="pb-3">
          {/* Ubah text-red-600 menjadi lebih terang (red-400/500) saat dark mode agar terbaca */}
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <FileX2 className="h-5 w-5" />
            <CardTitle className="text-lg font-semibold">
              {t("alert.announcement.admission.title")}
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="grid gap-4 text-sm">
          {/* The Message */}
          <p className="text-muted-foreground leading-relaxed">
            {t("alert.announcement.not-passed", {
              nama_lengkap: dataSiswa?.nama_lengkap,
              nomor_pendaftaran: dataPendaftaran?.nomor_pendaftaran,
              prodi_nama: isJalurInternasional
                ? dataSiswa?.prodi_name
                : dataSiswa?.prodi_nama,
            })}
          </p>

          {/* Structured Data Display */}
          {/* bg-muted/50 biasanya sudah adaptif, tapi border bisa dipertegas untuk dark mode */}
          <div className="bg-muted/50 p-4 rounded-md grid grid-cols-1 md:grid-cols-2 gap-3 border dark:border-slate-800">
            <div>
              <span className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t("dataSiswa.nama-lengkap")}
              </span>
              <span className="font-medium">{dataSiswa?.nama_lengkap}</span>
            </div>
            <div>
              <span className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t("dataPendaftaran.nomor-pendaftaran")}
              </span>
              <span className="font-mono">
                {dataPendaftaran?.nomor_pendaftaran}
              </span>
            </div>
            {(dataSiswa?.prodi_nama || dataSiswa?.prodi_name) && (
              <div className="md:col-span-2">
                <span className="block text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("dataPilihanProdi.prodi")}
                </span>
                <span className="font-medium">
                  {isJalurInternasional
                    ? dataSiswa?.prodi_name
                    : dataSiswa?.prodi_nama}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (dataPendaftaran && !dataPendaftaran?.is_lulus && isJalurInternasional) {
    return (
      <Card className="mb-5 border-l-4 border-l-amber-400 bg-amber-50/50 shadow-sm dark:bg-amber-950/10 dark:border-l-amber-500">
        <CardContent className="flex items-start gap-4 p-5">
          {/* Icon Section */}
          <div className="mt-1 flex-shrink-0">
            {/* Ubah bg-amber-100 menjadi dark:bg-amber-900/30 agar tidak terlalu terang di mode gelap */}
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <Timer className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>

          {/* Text Section */}
          <div className="space-y-1 w-full">
            {/* Label kecil untuk "Announcement :" */}
            <p className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
              Announcement :
            </p>

            {/* Heading dipindah ke sini agar lebih menonjol */}
            <h1 className="text-lg font-semibold text-gray-900 leading-tight dark:text-gray-50">
              Announcement Time
            </h1>

            {/* Deskripsi */}
            <p className="text-sm text-gray-600 leading-relaxed dark:text-gray-400">
              Still waiting for the UK (tuition fee) determination; it will be
              announced soon.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <main>
      {dataPendaftaran && dataPendaftaran?.is_lulus && !isJalurNasional && (
        <Card className="mb-5 border-green-200 bg-green-50/30 shadow-sm overflow-hidden relative dark:bg-green-900/10 dark:border-green-800">
          {/* Optional: Decorative accent */}
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <PartyPopper className="w-24 h-24 text-green-600 dark:text-green-500" />
          </div>

          <CardHeader className="pb-2">
            <div className="flex items-center gap-3 text-green-700 dark:text-green-400">
              <div className="p-2 bg-green-100 rounded-full dark:bg-green-900/30">
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <CardTitle className="text-xl font-bold">
                  {t("alert.announcement.admission.title")}
                </CardTitle>
                <p className="text-sm text-green-600 font-medium opacity-90 dark:text-green-300">
                  {t("alert.announcement.passed", {
                    nama_lengkap: dataSiswa?.nama_lengkap,
                    nomor_pendaftaran: dataPendaftaran?.nomor_pendaftaran,
                    prodi_nama: isJalurInternasional
                      ? dataSiswa?.prodi_name
                      : dataSiswa?.prodi_nama,
                  })}
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="mt-4">
            <div className="bg-white/80 backdrop-blur border border-green-100 rounded-lg p-5 grid gap-4 md:grid-cols-2 shadow-sm dark:bg-slate-900/50 dark:border-green-900">
              <div className="space-y-1">
                <p className="text-xs uppercase font-semibold text-gray-500 tracking-wider dark:text-gray-400">
                  {t("dataSiswa.nama-lengkap")}
                </p>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {dataSiswa?.nama_lengkap}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-xs uppercase font-semibold text-gray-500 tracking-wider dark:text-gray-400">
                  {t("dataPendaftaran.nomor-pendaftaran")}
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100">
                  {dataPendaftaran?.nomor_pendaftaran}
                </p>
              </div>
              <div className="md:col-span-2 space-y-1">
                <p className="text-xs uppercase font-semibold text-gray-500 tracking-wider dark:text-gray-400">
                  {t("dataPilihanProdi.prodi")}
                </p>
                <p className="text-lg font-bold text-green-700 dark:text-green-400">
                  {isJalurInternasional
                    ? dataSiswa?.prodi_name
                    : dataSiswa?.prodi_nama}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Alert
        variant={"default"}
        className="mb-5 border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/10 dark:border-l-amber-500"
      >
        <div className="flex items-start gap-4">
          {/* Ubah bg-amber-100 menjadi dark:bg-amber-900/30 agar tidak terlalu terang di mode gelap */}
          <div className="p-2 bg-amber-100 rounded-full mt-1 dark:bg-amber-900/30">
            <Info className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>

          <div className="w-full">
            <AlertTitle className="mb-4 text-lg font-bold text-gray-800 dark:text-gray-100">
              {t("alert.announcement.ukt.title")} :
            </AlertTitle>

            <AlertDescription className="text-sm text-gray-700 dark:text-gray-300">
              {isJalurInternasional ? (
                <ul className="space-y-4">
                  {/* Item 1: Payment Dates */}
                  <li className="flex gap-4 items-start">
                    <CalendarClock className="h-5 w-5 text-blue-600 mt-0.5 shrink-0 dark:text-blue-400" />
                    <div className="leading-relaxed">
                      Payment for UKT can be made starting from{" "}
                      <span className="font-bold text-blue-700 bg-blue-50 px-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                        {dataPeriode?.waktu_mulai_bayar_ukt &&
                          formatDate(
                            dataPeriode?.waktu_mulai_bayar_ukt || "",
                            "medium",
                            "en-Us",
                          )}
                      </span>{" "}
                      until{" "}
                      <span className="font-bold text-blue-700 bg-blue-50 px-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                        {(dataPeriodeCustom?.custom_waktu_selesai_isi_ukt ||
                          dataPeriode?.waktu_selesai_bayar_ukt) &&
                          formatDate(
                            dataPeriodeCustom?.custom_waktu_selesai_isi_ukt ||
                              dataPeriode?.waktu_selesai_bayar_ukt ||
                              "",
                            "medium",
                            "en-Us",
                          )}
                      </span>
                    </div>
                  </li>

                  {/* Item 2: Warning */}
                  <li className="flex gap-4 items-start">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0 dark:text-red-400" />
                    <div className="leading-relaxed">
                      If you do not make the UKT payment by the specified
                      deadline, you will be considered as{" "}
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        withdrawing
                      </span>{" "}
                      from {web.univAcronym}.
                    </div>
                  </li>

                  {/* Item 3: Action */}
                  <li className="flex gap-4 items-start">
                    <FileDown className="h-5 w-5 text-amber-600 mt-0.5 shrink-0 dark:text-amber-400" />
                    <div className="leading-relaxed">
                      After making{" "}
                      {isPenerimaKIPK ? "KIP-K Confirmation" : "UKT payment"},
                      you are required to print the Student Identification
                      Number (NIM) via the "Download" button.
                    </div>
                  </li>

                  {/* Item 4: Completion */}
                  <li className="flex gap-4 items-start">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0 dark:text-green-400" />
                    <div className="leading-relaxed">
                      Re-registration is considered complete once the
                      prospective student has received the NIM.
                    </div>
                  </li>
                </ul>
              ) : (
                <ul className="space-y-4">
                  {/* Item 1: Dates (KIPK or Regular) */}
                  <li className="flex gap-4 items-start">
                    <CalendarClock className="h-5 w-5 text-blue-600 mt-0.5 shrink-0 dark:text-blue-400" />
                    {isPenerimaKIPK ? (
                      <div className="leading-relaxed">
                        Bagi penerima KIP-K, anda wajib konfirmasi penerima
                        KIP-K hingga{" "}
                        <span className="font-bold text-blue-700 bg-blue-50 px-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                          {dataPeriode?.waktu_selesai_konfirmasi_kipk &&
                            formatDateIndonesia(
                              dataPeriode?.waktu_selesai_konfirmasi_kipk,
                            )}
                        </span>
                      </div>
                    ) : (
                      <div className="leading-relaxed">
                        Pembayaran UKT {isJalurMandiri ? "dan IPI" : ""} dapat
                        dilakukan mulai{" "}
                        <span className="font-bold text-blue-700 bg-blue-50 px-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                          {dataPeriode?.waktu_mulai_bayar_ukt &&
                            formatDateIndonesia(
                              dataPeriode?.waktu_mulai_bayar_ukt || "",
                              "medium",
                            )}
                        </span>{" "}
                        hingga{" "}
                        <span className="font-bold text-blue-700 bg-blue-50 px-1 rounded dark:bg-blue-900/30 dark:text-blue-300">
                          {(dataPeriodeCustom?.custom_waktu_selesai_bayar_ukt ||
                            dataPeriode?.waktu_selesai_bayar_ukt) &&
                            formatDateIndonesia(
                              dataPeriodeCustom?.custom_waktu_selesai_bayar_ukt ||
                                dataPeriode?.waktu_selesai_bayar_ukt ||
                                "",
                              "medium",
                            )}
                        </span>
                      </div>
                    )}
                  </li>

                  {/* Item 2: Warning */}
                  <li className="flex gap-4 items-start">
                    <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0 dark:text-red-400" />
                    {isPenerimaKIPK ? (
                      <div className="leading-relaxed">
                        Jika Anda tidak melakukan konfirmasi status penerima
                        KIP-K hingga batas waktu yang ditentukan, Anda akan
                        dianggap
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          {" "}
                          mengundurkan diri
                        </span>{" "}
                        dari {web.univAcronym}.
                      </div>
                    ) : (
                      <div className="leading-relaxed">
                        Jika Anda tidak melakukan pembayaran UKT hingga batas
                        waktu yang ditentukan, Anda akan dianggap{" "}
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          mengundurkan diri
                        </span>{" "}
                        dari {web.univAcronym}.
                      </div>
                    )}
                  </li>

                  {/* Item 3: Action */}
                  <li className="flex gap-4 items-start">
                    <FileDown className="h-5 w-5 text-amber-600 mt-0.5 shrink-0 dark:text-amber-400" />
                    <div className="leading-relaxed">
                      Setelah melakukan{" "}
                      {isPenerimaKIPK ? "Konfirmasi KIP-K" : "pembayaran UKT"} ,
                      anda wajib melakukan cetak Nomor Induk Mahasiswa (NIM)
                      melalui tombol "Card Cetak NIM".
                    </div>
                  </li>

                  {/* Item 4: Completion */}
                  <li className="flex gap-4 items-start">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0 dark:text-green-400" />
                    <div className="leading-relaxed">
                      Pendaftaran Kembali diangap selesai jika calon mahasiswa
                      sudah mendapatkan NIM.
                    </div>
                  </li>
                </ul>
              )}
            </AlertDescription>
          </div>
        </div>
      </Alert>

      {!dataPendaftaran ||
      isPendingDataSiswa ||
      isPendingDataStatusPembayaran ||
      isPendingPeriode ? (
        <div className="flex flex-col gap-y-4">
          <Skeleton className="h-[200px] w-full rounded-xl" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-[150px] w-full rounded-xl" />
            <Skeleton className="h-[150px] w-full rounded-xl" />
            <Skeleton className="h-[150px] w-full rounded-xl" />
          </div>
        </div>
      ) : (
        <section className="space-y-6">
          {/* === MAIN PROFILE & STATUS CARD === */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* === LEFT COLUMN: PROFILE & STATUS (Takes up 8/12 columns) === */}
            <div className={` space-y-6 lg:col-span-8`}>
              <Card className="overflow-hidden border-t-4 border-t-primary shadow-sm h-full dark:border-gray-800 dark:bg-gray-950">
                <CardHeader className="bg-slate-50/50 pb-6 border-b border-slate-100 dark:bg-gray-900/50 dark:border-gray-800">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      {!isWajibSanggahBerkas && !isPenerimaKIPK && (
                        <CardTitle className="text-xl font-bold text-primary flex items-center gap-2">
                          <Banknote className="w-5 h-5" />
                          {t("ukt.announcement.title")}
                        </CardTitle>
                      )}
                      <CardDescription className="mt-1 dark:text-gray-400">
                        Data Pendaftaran & Status Pembayaran
                      </CardDescription>
                    </div>

                    {/* Global Status Badge */}
                    <div className="flex items-center gap-2">
                      {isPenerimaKIPK ? (
                        isSudahKonfirmasiKIPK ? (
                          <Badge className="bg-green-600 hover:bg-green-700 px-3 py-1.5 text-sm dark:bg-green-700 dark:hover:bg-green-600">
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Sudah
                            Konfirmasi KIP-K
                          </Badge>
                        ) : (
                          <Badge
                            variant="destructive"
                            className="px-3 py-1.5 text-sm animate-pulse"
                          >
                            <AlertCircle className="w-4 h-4 mr-2" /> Belum
                            Konfirmasi KIP-K
                          </Badge>
                        )
                      ) : isSudahBayar ? (
                        isPenerimaBeasiswaPenuh ? (
                          <Badge className="bg-green-600 hover:bg-green-700 px-3 py-1.5 text-sm dark:bg-green-700 dark:hover:bg-green-600">
                            <CheckCircle2 className="w-4 h-4 mr-2" />{" "}
                            {t("ukt.payment.status-5")}
                          </Badge>
                        ) : (
                          <Badge className="bg-green-600 hover:bg-green-700 px-3 py-1.5 text-sm dark:bg-green-700 dark:hover:bg-green-600">
                            <CheckCircle2 className="w-4 h-4 mr-2" />{" "}
                            {t("ukt.payment.status-2")}
                          </Badge>
                        )
                      ) : isAfirmasi ? (
                        <Badge className="bg-blue-600 hover:bg-blue-700 px-3 py-1.5 text-sm dark:bg-blue-700 dark:hover:bg-blue-600">
                          Sudah dibayar (Afirmasi)
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="px-3 py-1.5 text-sm border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-400"
                        >
                          <Clock className="w-4 h-4 mr-2" />{" "}
                          {t("ukt.payment.status-1")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-8">
                    {/* Avatar Section */}
                    <div className="flex flex-row md:flex-col items-center md:items-start gap-4 min-w-[200px]">
                      <Avatar className="w-20 h-20 md:w-28 md:h-28 border-4 border-white shadow-md ring-1 ring-slate-100 dark:border-gray-800 dark:ring-gray-800">
                        <AvatarImage
                          src={dataSiswa?.path_foto || ""}
                          alt={"foto-siswa"}
                          className="object-cover"
                        />
                        <AvatarFallback className="text-2xl bg-slate-100 text-slate-400 font-bold dark:bg-gray-800 dark:text-gray-500">
                          {getInitials(dataSiswa?.nama_lengkap || "")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="text-left">
                        <h3 className="font-bold text-lg leading-tight dark:text-gray-100">
                          {dataSiswa?.nama_lengkap}
                        </h3>
                        <p className="text-sm text-muted-foreground font-mono mt-1 dark:text-gray-400">
                          {dataPendaftaran?.nomor_pendaftaran}
                        </p>
                        {dataPendaftaran?.nim && (
                          <Badge
                            variant="outline"
                            className="mt-2 border-primary/20 text-primary bg-primary/5 dark:bg-primary/10"
                          >
                            NIM: {dataPendaftaran?.nim}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="flex-1 w-full space-y-6">
                      {/* Validation Alert */}
                      {isWajibSanggahBerkas && (
                        <Alert className="mb-6 border-l-4 border-l-red-500 border-t border-r border-b border-red-100 bg-red-50/50 dark:bg-red-900/10 dark:border-red-900/50">
                          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                            <div className="flex gap-4">
                              <div className="mt-1 p-2 h-10 w-10 flex justify-center items-center bg-red-100 rounded-full shrink-0 dark:bg-red-900/30">
                                <FileWarning className="h-5 w-5 text-red-600 dark:text-red-400" />
                              </div>
                              <div className="space-y-1">
                                <AlertTitle className="text-lg font-bold text-red-900 dark:text-red-300">
                                  Status Pendaftaran:{" "}
                                  <span className="underline decoration-wavy decoration-red-400">
                                    Ditangguhkan
                                  </span>
                                </AlertTitle>
                                <AlertDescription className="text-sm text-red-800/90 leading-relaxed max-w-2xl dark:text-red-200/80">
                                  Terdapat berkas persyaratan yang dinyatakan{" "}
                                  <strong>tidak valid</strong> atau belum
                                  lengkap. Mohon segera perbaiki berkas Anda
                                  agar proses verifikasi dapat dilanjutkan.
                                </AlertDescription>
                              </div>
                            </div>

                            {/* Tombol aksi langsung untuk scroll ke tabel/modal upload */}
                            <Button
                              variant="destructive"
                              size="sm"
                              className="shrink-0 w-full sm:w-auto shadow-sm bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
                              onClick={() => {
                                document
                                  .getElementById("tabel-sanggah-berkas")
                                  ?.scrollIntoView({ behavior: "smooth" });
                              }}
                            >
                              Perbaiki Berkas{" "}
                              <ArrowRight className="ml-2 h-4 w-4 lg:rotate-0 rotate-90" />
                            </Button>
                          </div>
                        </Alert>
                      )}

                      {/* Data Details */}
                      {!isWajibSanggahBerkas && (
                        <div className="grid grid-cols-1 gap-y-5">
                          {/* Academic Info */}
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 dark:bg-gray-900/50 dark:border-gray-800">
                            {/* Ubah gap-3 menjadi gap-6 agar jarak antar kolom lebih lega */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                              {/* Item 1: Email */}
                              {/* min-w-0 penting di dalam flex/grid child agar text-wrapping berfungsi */}
                              <div className="min-w-0">
                                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1 dark:text-slate-500">
                                  Email SSO
                                </span>
                                {/* Tambahkan 'break-all' atau 'break-words' agar email panjang turun ke bawah */}
                                <span className="font-medium text-slate-800 break-all block dark:text-slate-200">
                                  {dataPendaftaran?.email_sso || "-"}
                                </span>
                              </div>

                              {/* Item 2: Prodi */}
                              <div className="min-w-0">
                                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1 dark:text-slate-500">
                                  Program Studi
                                </span>
                                {/* Tambahkan 'break-words' dan 'leading-snug' agar nama prodi panjang terlihat rapi */}
                                <span className="font-medium text-primary break-words block leading-snug">
                                  {isJalurInternasional
                                    ? dataSiswa?.prodi_name
                                    : dataSiswa?.prodi_nama}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Financial Info */}
                          <div className="space-y-4">
                            {isJalurPascasarjana &&
                            dataPendaftaran?.data_penetapan_ukt
                              ?.is_cicil_bayar ? (
                              <>
                                <div className="flex justify-between items-end border-b border-dashed pb-2 dark:border-gray-700">
                                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                    Nominal UKT
                                  </span>
                                  <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                    {dataPendaftaran?.data_penetapan_ukt
                                      ?.nominal_ukt
                                      ? formatRupiah(
                                          Number(
                                            dataPendaftaran?.data_penetapan_ukt
                                              ?.nominal_ukt,
                                          ),
                                        )
                                      : "-"}
                                  </span>
                                </div>
                                {/* <div className="flex justify-between items-end border-b border-dashed pb-2 dark:border-gray-700">
                                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                    Tagihan Cicilan Tahap 1
                                  </span>
                                  <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                    {dataPendaftaran?.data_penetapan_ukt
                                      ?.total_tagihan
                                      ? formatRupiah(
                                          Number(
                                            dataPendaftaran?.data_penetapan_ukt
                                              ?.total_tagihan,
                                          ),
                                        )
                                      : "-"}
                                  </span>
                                </div> */}
                              </>
                            ) : (
                              <div className="flex justify-between items-end border-b border-dashed pb-2 dark:border-gray-700">
                                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                  Tagihan UKT
                                </span>
                                <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                  {isPenerimaKIPK ? (
                                    <span className="text-green-600 dark:text-green-400">
                                      Rp 0 (KIP-K)
                                    </span>
                                  ) : dataPendaftaran?.data_penetapan_ukt
                                      ?.nominal_ukt ? (
                                    formatRupiah(
                                      Number(
                                        dataPendaftaran?.data_penetapan_ukt
                                          ?.nominal_ukt,
                                      ),
                                    )
                                  ) : (
                                    "-"
                                  )}
                                </span>
                              </div>
                            )}

                            {isJalurMandiri && !isPenerimaKIPK && (
                              <div className="flex justify-between items-end border-b border-dashed pb-2 dark:border-gray-700">
                                <div className="flex flex-col gap-y-1">
                                  <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                                    IPI (Iuran Pengembangan Institusi)
                                  </p>
                                  <p className="text-xs font-medium text-slate-400 dark:text-slate-400 flex items-center gap-3">
                                    <MdInfo size={20} />
                                    IPI dibayarkan satu kali selama kuliah dan
                                    tidak dapat dicicil.
                                  </p>
                                </div>
                                <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                                  {dataPendaftaran?.data_penetapan_ukt
                                    ?.nominal_ipi
                                    ? formatRupiah(
                                        Number(
                                          dataPendaftaran?.data_penetapan_ukt
                                            ?.nominal_ipi,
                                        ),
                                      )
                                    : "-"}
                                </span>
                              </div>
                            )}

                            {isJalurMandiri && !isPenerimaKIPK && (
                              <div className="flex justify-between items-center bg-primary/5 p-3 rounded-lg border border-primary/10 dark:bg-primary/10 dark:border-primary/20">
                                <span className="font-bold text-primary">
                                  Total Tagihan
                                </span>
                                <span className="text-xl font-black text-primary">
                                  {dataPendaftaran?.data_penetapan_ukt
                                    ?.total_tagihan ? (
                                    formatRupiah(
                                      Number(
                                        dataPendaftaran?.data_penetapan_ukt
                                          ?.total_tagihan,
                                      ),
                                    )
                                  ) : (
                                    <span className="text-sm font-normal text-red-500 dark:text-red-400">
                                      {t("ukt.announcement.determination")}
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}

                            {dataPendaftaran?.data_penetapan_ukt
                              ?.is_cicil_bayar ? (
                              <div className="flex justify-between items-center bg-primary/5 p-3 rounded-lg border border-primary/10 dark:bg-primary/10 dark:border-primary/20">
                                <span className="font-bold text-primary">
                                  Nominal Cicilan Awal
                                </span>
                                <span className="text-xl font-black text-primary">
                                  {formatRupiah(
                                    Number(
                                      dataPendaftaran?.data_penetapan_ukt
                                        ?.nominal_cicilan_awal,
                                    ),
                                  )}
                                </span>
                              </div>
                            ) : (
                              ""
                            )}
                          </div>

                          {/* Contextual Actions (KIPK Confirmation / Invoice) */}
                          {isPenerimaKIPK && !isSudahKonfirmasiKIPK && (
                            <Button
                              onClick={() => setShowConfirmKIPK(true)}
                              className="w-full bg-primary text-white shadow-sm h-12 text-base"
                            >
                              <LuCheck className="mr-2 h-5 w-5" /> Konfirmasi
                              Penerima KIP-K
                            </Button>
                          )}

                          {!isPenerimaKIPK && !isAfirmasi && (
                            <div className="space-y-3">
                              {((dataPeriode?.waktu_selesai_bayar_ukt &&
                                !timePassed?.waktu_selesai_bayar_ukt) ||
                                (dataPeriodeActive?.waktu_selesai_bayar_ukt &&
                                  !timePassedActive?.waktu_selesai_bayar_ukt_selesai) ||
                                isSudahBayar) && (
                                <div className="space-y-3">
                                  {/* Logic for showing payment buttons if invoice exists */}
                                  {listTagihanUkt?.length > 0 && (
                                    <div
                                      className={`grid ${isSudahBayar ? "grid-cols-1" : "grid-cols-2"} gap-3`}
                                    >
                                      <Button
                                        className={`h-11 ${isSudahBayar ? "bg-slate-900 text-white dark:bg-slate-700" : ""}`}
                                        onClick={() => {
                                          setShowTagihan(true);
                                        }}
                                      >
                                        {isSudahBayar
                                          ? t("ukt.button.paid.payment.view")
                                          : t("ukt.button.unpaid.payment.view")}
                                      </Button>
                                      {!isSudahBayar && (
                                        <Button
                                          variant="outline"
                                          className="h-11 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                                          onClick={() =>
                                            setShowGenerateTagihan(true)
                                          }
                                        >
                                          {t(
                                            "ukt.button.unpaid.invoice.regenerate",
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                  )}

                                  {/* Logic for Generate Invoice Button (Empty State) */}
                                  {listTagihanUkt?.length === 0 &&
                                    !isWajibSanggahBerkas &&
                                    !isSudahBayar && (
                                      <Button
                                        onClick={() =>
                                          setShowGenerateTagihan(true)
                                        }
                                        disabled={
                                          !sudahMulaiBayar ||
                                          !dataPendaftaran?.data_penetapan_ukt
                                        }
                                        className="w-full h-12 text-base"
                                      >
                                        <CreditCard className="mr-2 h-5 w-5" />{" "}
                                        {t("ukt.button.unpaid.invoice")}
                                      </Button>
                                    )}
                                </div>
                              )}

                              {/* Surat Pernyataan Links */}
                              {!isJalurPascasarjana &&
                                !isJalurProfesi &&
                                !isJalurInternasional && (
                                  <div className="flex flex-wrap items-center justify-center gap-4 text-xs pt-2">
                                    {(() => {
                                      const isUploadedSuratPernyataan =
                                        !!dataPendaftaran?.path_surat_pernyataan_ukt;

                                      const isPaymentTimeActive =
                                        (dataPeriode?.waktu_selesai_bayar_ukt &&
                                          !timePassed?.waktu_selesai_bayar_ukt) ||
                                        (dataPeriodeActive?.waktu_selesai_bayar_ukt &&
                                          !timePassedActive?.waktu_selesai_bayar_ukt_selesai);

                                      return (
                                        <>
                                          {isUploadedSuratPernyataan && (
                                            <span
                                              className="text-primary font-medium cursor-pointer hover:underline flex items-center"
                                              onClick={() =>
                                                setShowSuratPernyataanUKT(true)
                                              }
                                            >
                                              <FileText className="w-3 h-3 mr-1" />{" "}
                                              Lihat Surat Pernyataan
                                            </span>
                                          )}

                                          {!isSudahBayar &&
                                            isPaymentTimeActive &&
                                            listTagihanUkt?.length > 0 && (
                                              <Button
                                                variant={"outline"}
                                                onClick={() =>
                                                  setShowUploadUlangSurat(true)
                                                }
                                                className="flex-1"
                                              >
                                                <AlertCircle className="w-3 h-3 mr-1" />
                                                Upload Ulang Surat Pernyataan
                                              </Button>
                                            )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* === RIGHT COLUMN: ACTION STEPS (Takes up 4/12 columns) === */}

            {isWajibSanggahBerkas ? (
              <div className="lg:col-span-4 space-y-4 sticky top-4 animate-in fade-in duration-500">
                <h3 className="text-sm font-bold text-rose-500 uppercase tracking-wider mb-2 px-1 dark:text-rose-400">
                  Tindakan Diperlukan
                </h3>

                <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-xl p-5 shadow-sm relative overflow-hidden">
                  {/* Aksen background */}
                  <div className="absolute -right-4 -top-4 bg-rose-100 dark:bg-rose-900/30 w-24 h-24 rounded-full blur-2xl opacity-60 pointer-events-none"></div>

                  <div className="flex items-start gap-4 mb-5 relative z-10">
                    <div className="bg-rose-100 text-rose-600 dark:bg-rose-900/50 dark:text-rose-400 p-2.5 rounded-lg shrink-0">
                      <AlertCircle size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-rose-900 dark:text-rose-100 text-sm mb-1">
                        Perbaikan Berkas (Sanggah)
                      </h4>
                      <p className="text-xs text-rose-700 dark:text-rose-300 leading-relaxed">
                        Ada berkas Anda yang ditolak oleh verifikator. Silakan
                        periksa tabel di bawah dan unggah ulang berkas yang
                        sesuai.
                      </p>
                    </div>
                  </div>

                  {/* Progress Section */}
                  <div className="space-y-2 mt-2 pt-4 border-t border-rose-200/60 dark:border-rose-800/60 relative z-10">
                    <div className="flex justify-between items-end text-xs">
                      <span className="font-semibold text-rose-800 dark:text-rose-200">
                        Progress Perbaikan
                      </span>
                      <span className="font-bold text-rose-600 dark:text-rose-400">
                        {berkasSelesaiSanggah} / {totalBerkasSanggah} Berkas
                      </span>
                    </div>

                    {/* Progress Bar Container */}
                    <div className="h-2.5 w-full bg-rose-200 dark:bg-rose-900/50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-rose-600 dark:bg-rose-500 transition-all duration-700 ease-out rounded-full"
                        style={{ width: `${progressSanggah}%` }}
                      />
                    </div>

                    {/* Instruksi Tambahan (Dinamis) */}
                    <div className="flex items-start gap-1.5 mt-3 text-[11px] text-rose-600 dark:text-rose-400 font-medium">
                      <ArrowDownToLine className="w-3.5 h-3.5 shrink-0 mt-0.5 animate-bounce" />
                      {progressSanggah === 100 ? (
                        <span>
                          Semua berkas telah diunggah! Menunggu verifikasi ulang
                          oleh panitia.
                        </span>
                      ) : (
                        <span>
                          Gulir ke tabel bawah, cari berkas dengan status
                          ditolak, lalu klik tombol unggah.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // <div className="lg:col-span-4 space-y-4 sticky top-4">
              //   <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 px-1 dark:text-slate-400">
              //     Langkah Selanjutnya
              //   </h3>

              //   {!isAfirmasi && !isPenerimaKIPK && (
              //     <>
              //       {/* STEP 1 */}
              //       <div
              //         onClick={() => setShowGenerateTagihan(true)}
              //         className={`group relative bg-white border rounded-xl p-4 transition-all duration-200 cursor-pointer hover:shadow-md hover:border-blue-300 dark:bg-gray-950 dark:border-gray-800 dark:hover:border-blue-700 ${
              //           !sudahMulaiBayar ||
              //           !dataPendaftaran?.data_penetapan_ukt ||
              //           listTagihanUkt?.length > 0 ||
              //           isWajibSanggahBerkas
              //             ? "opacity-60 grayscale cursor-not-allowed pointer-events-none"
              //             : "border-l-4 border-l-blue-500"
              //         }`}
              //       >
              //         <div className="flex items-start gap-4">
              //           <div className="bg-blue-50 text-blue-600 p-2.5 rounded-lg shrink-0 group-hover:bg-blue-600 group-hover:text-white transition-colors dark:bg-blue-900/20 dark:text-blue-400">
              //             <AiFillBank size={20} />
              //           </div>
              //           <div>
              //             <h4 className="font-bold text-slate-900 text-sm mb-1 dark:text-gray-100">
              //               {t("ukt.card.title.1")}
              //             </h4>
              //             <p className="text-xs text-slate-500 leading-relaxed dark:text-slate-400">
              //               {t("ukt.card.description.1")}
              //             </p>
              //           </div>
              //         </div>
              //       </div>
              //       {/* STEP 2 */}
              //       <div
              //         onClick={() => mutateGenerateKuitansi()}
              //         className={`group relative bg-white border rounded-xl p-4 transition-all duration-200 cursor-pointer hover:shadow-md hover:border-amber-300 dark:bg-gray-950 dark:border-gray-800 dark:hover:border-amber-700 ${
              //           !dataPendaftaran?.data_penetapan_ukt ||
              //           listTagihanUkt?.length === 0 ||
              //           !isFetchedListTagihanUkt ||
              //           (isPenerimaKIPK && !isSudahKonfirmasiKIPK) ||
              //           isWajibSanggahBerkas
              //             ? "opacity-60 grayscale cursor-not-allowed pointer-events-none"
              //             : "border-l-4 border-l-amber-500"
              //         }`}
              //       >
              //         <div className="flex items-start gap-4">
              //           <div className="bg-amber-50 text-amber-600 p-2.5 rounded-lg shrink-0 group-hover:bg-amber-600 group-hover:text-white transition-colors dark:bg-amber-900/20 dark:text-amber-400">
              //             <FaFilePdf size={20} />
              //           </div>
              //           <div className="flex-1">
              //             <h4 className="font-bold text-slate-900 text-sm mb-1 dark:text-gray-100">
              //               {isPenerimaKIPK
              //                 ? "Cetak Bukti KIP-K"
              //                 : isSudahBayar
              //                   ? t("ukt.card.title.2.2")
              //                   : t("ukt.card.title.2")}
              //             </h4>
              //             <p className="text-xs text-slate-500 leading-relaxed mb-3 dark:text-slate-400">
              //               {isPenerimaKIPK
              //                 ? "Unduh bukti penerima KIP-K."
              //                 : t("ukt.card.description.2")}
              //             </p>
              //             {(!isPenerimaKIPK ||
              //               (isPenerimaKIPK && isSudahKonfirmasiKIPK)) &&
              //               isFetchedListTagihanUkt &&
              //               listTagihanUkt?.length > 0 && (
              //                 <Button
              //                   variant="secondary"
              //                   size="sm"
              //                   className="w-full h-8 text-xs dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              //                   disabled={isPendingGenerateKuitansi}
              //                 >
              //                   {isPendingGenerateKuitansi ? (
              //                     <Loader2 className="w-3 h-3 animate-spin mr-2" />
              //                   ) : (
              //                     <Download className="w-3 h-3 mr-2" />
              //                   )}
              //                   Download PDF
              //                 </Button>
              //               )}
              //           </div>
              //         </div>
              //       </div>
              //     </>
              //   )}

              //   {/* STEP 3 */}
              //   <div
              //     onClick={handleGenerateAkun}
              //     className={`group relative bg-white border rounded-xl p-4 transition-all duration-200 cursor-pointer hover:shadow-md hover:border-green-300 dark:bg-gray-950 dark:border-gray-800 dark:hover:border-green-700 ${
              //       (!isPenerimaBeasiswaPenuh && !isSudahBayar) ||
              //       (isPenerimaKIPK && !isSudahKonfirmasiKIPK) ||
              //       isWajibSanggahBerkas
              //         ? "opacity-60 grayscale cursor-not-allowed pointer-events-none"
              //         : "border-l-4 border-l-green-500"
              //     }`}
              //   >
              //     <div className="flex items-start gap-4">
              //       <div className="bg-green-50 text-green-600 p-2.5 rounded-lg shrink-0 group-hover:bg-green-600 group-hover:text-white transition-colors dark:bg-green-900/20 dark:text-green-400">
              //         <Printer size={20} />
              //       </div>
              //       <div className="flex-1">
              //         <h4 className="font-bold text-slate-900 text-sm mb-1 dark:text-gray-100">
              //           {t("ukt.card.title.3")}
              //         </h4>
              //         <p className="text-xs text-slate-500 leading-relaxed mb-3 dark:text-slate-400">
              //           {t("ukt.card.description.3")}
              //         </p>

              //         {(isSudahBayar ||
              //           isSudahKonfirmasiKIPK ||
              //           isAfirmasi) && (
              //           <Button
              //             variant="secondary"
              //             size="sm"
              //             className="w-full h-8 text-xs dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              //             disabled={isPending}
              //           >
              //             {isPending ? (
              //               <Loader2 className="w-3 h-3 animate-spin mr-2" />
              //             ) : (
              //               <Download className="w-3 h-3 mr-2" />
              //             )}
              //             Cetak NIM
              //           </Button>
              //         )}
              //       </div>
              //     </div>
              //   </div>
              // </div>

              <div className="lg:col-span-4 space-y-4 sticky top-4">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 px-1 dark:text-slate-400 flex items-center gap-2">
                  {/* Ikon tambahan opsional untuk mempertegas "Langkah" */}
                  <ListChecks className="w-4 h-4" />
                  Langkah Selanjutnya
                </h3>

                {/* Menentukan urutan nomor berdasarkan kondisi */}
                {(() => {
                  // Jika bukan KIPK/Afirmasi, maka ada 3 langkah. Jika iya, maka hanya 1 langkah (Cetak NIM).
                  const hasPaymentSteps = !isAfirmasi && !isPenerimaKIPK;
                  let currentStep = 1;

                  return (
                    <div className="relative space-y-3">
                      {/* Garis vertikal penghubung antar step (Timeline connector) */}
                      <div className="absolute left-[1.35rem] top-6 bottom-6 w-0.5 bg-slate-200 dark:bg-slate-800 z-0 hidden sm:block" />

                      {hasPaymentSteps && (
                        <>
                          {/* --- STEP 1: GENERATE TAGIHAN --- */}
                          {(() => {
                            const stepNum = currentStep++;
                            const isLocked =
                              !sudahMulaiBayar ||
                              !dataPendaftaran?.data_penetapan_ukt ||
                              listTagihanUkt?.length > 0 ||
                              isWajibSanggahBerkas;
                            const isCompleted = listTagihanUkt?.length > 0;

                            return (
                              <div
                                onClick={() =>
                                  !isLocked &&
                                  ((dataPeriode?.waktu_selesai_bayar_ukt &&
                                    !timePassed?.waktu_selesai_bayar_ukt) ||
                                    (dataPeriodeActive?.waktu_selesai_bayar_ukt &&
                                      !timePassedActive?.waktu_selesai_bayar_ukt_selesai)) &&
                                  !isCompleted &&
                                  setShowGenerateTagihan(true)
                                }
                                className={`group relative bg-white border rounded-xl p-4 transition-all duration-300 z-10 
                    ${
                      isLocked
                        ? "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-not-allowed pointer-events-none"
                        : isCompleted
                          ? "border-green-300 bg-green-50/50 dark:bg-green-900/10 dark:border-green-900/50 pointer-events-none" // Jika sudah, ubah jadi hijau
                          : "cursor-pointer hover:shadow-md hover:border-blue-300 border-blue-100 shadow-sm dark:bg-slate-950 dark:border-blue-900/50"
                    }
                  `}
                              >
                                <div
                                  className={`flex items-start gap-4 ${isLocked ? "opacity-60 grayscale" : ""}`}
                                >
                                  {/* Indikator Nomor / Checkmark */}
                                  <div
                                    className={`relative z-10 w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ring-4 ring-white dark:ring-slate-950 transition-colors
                        ${
                          isCompleted
                            ? "bg-green-500 text-white"
                            : isLocked
                              ? "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                              : "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                        }
                      `}
                                  >
                                    {isCompleted ? (
                                      <Check className="w-3.5 h-3.5" />
                                    ) : (
                                      stepNum
                                    )}
                                  </div>

                                  <div className="flex-1 pt-0.5">
                                    <div className="flex items-center justify-between mb-1">
                                      <h4
                                        className={`font-bold text-sm ${isCompleted ? "text-green-700 dark:text-green-400" : "text-slate-900 dark:text-gray-100"}`}
                                      >
                                        {t("ukt.card.title.1")}
                                      </h4>
                                      {/* Ikon Aksi (hanya muncul jika aktif) */}
                                      {!isLocked && !isCompleted && (
                                        <ChevronRight className="w-4 h-4 text-blue-400 group-hover:translate-x-1 transition-transform" />
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500 leading-relaxed dark:text-slate-400">
                                      {isCompleted
                                        ? "Tagihan berhasil dibuat."
                                        : t("ukt.card.description.1")}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* --- STEP 2: CETAK TAGIHAN (KUITANSI) --- */}
                          {(() => {
                            const stepNum = currentStep++;
                            const isLocked =
                              !dataPendaftaran?.data_penetapan_ukt ||
                              listTagihanUkt?.length === 0 ||
                              !isFetchedListTagihanUkt ||
                              (isPenerimaKIPK && !isSudahKonfirmasiKIPK) ||
                              isWajibSanggahBerkas;
                            const isCompleted = isSudahBayar; // Asumsi jika sudah bayar, step ini "Selesai" atau minimal sudah lewat.

                            return (
                              <div
                                onClick={() =>
                                  !isLocked && mutateGenerateKuitansi()
                                }
                                className={`group relative bg-white border rounded-xl p-4 transition-all duration-300 z-10
                    ${
                      isLocked
                        ? "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-not-allowed pointer-events-none"
                        : isCompleted
                          ? "border-green-300 bg-green-50/50 dark:bg-green-900/10 dark:border-green-900/50" // Bisa tetap diklik jika butuh cetak kuitansi lunas
                          : "cursor-pointer hover:shadow-md hover:border-amber-300 border-amber-100 shadow-sm dark:bg-slate-950 dark:border-amber-900/50"
                    }
                  `}
                              >
                                <div
                                  className={`flex items-start gap-4 ${isLocked ? "opacity-60 grayscale" : ""}`}
                                >
                                  <div
                                    className={`relative z-10 w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ring-4 ring-white dark:ring-slate-950 transition-colors
                        ${
                          isCompleted
                            ? "bg-green-500 text-white"
                            : isLocked
                              ? "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                              : "bg-amber-500 text-white shadow-md shadow-amber-500/30"
                        }
                      `}
                                  >
                                    {isCompleted ? (
                                      <Check className="w-3.5 h-3.5" />
                                    ) : (
                                      stepNum
                                    )}
                                  </div>

                                  <div className="flex-1 pt-0.5">
                                    <div className="flex items-center justify-between mb-1">
                                      <h4
                                        className={`font-bold text-sm ${isCompleted ? "text-green-700 dark:text-green-400" : "text-slate-900 dark:text-gray-100"}`}
                                      >
                                        {isPenerimaKIPK
                                          ? "Cetak Bukti KIP-K"
                                          : isSudahBayar
                                            ? t("ukt.card.title.2.2")
                                            : t("ukt.card.title.2")}
                                      </h4>
                                      {!isLocked && !isCompleted && (
                                        <ChevronRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                                      )}
                                    </div>

                                    <p className="text-xs text-slate-500 leading-relaxed mb-3 dark:text-slate-400">
                                      {isPenerimaKIPK
                                        ? "Unduh bukti penerima KIP-K."
                                        : isCompleted
                                          ? "Pembayaran telah dikonfirmasi."
                                          : t("ukt.card.description.2")}
                                    </p>

                                    {/* Tombol Cetak PDF */}
                                    {!isLocked && (
                                      <Button
                                        variant={
                                          isCompleted ? "outline" : "secondary"
                                        }
                                        size="sm"
                                        className={`w-full h-8 text-xs ${isCompleted ? "border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800/50 dark:text-green-400 dark:hover:bg-green-900/20" : "dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"}`}
                                        disabled={isPendingGenerateKuitansi}
                                      >
                                        {isPendingGenerateKuitansi ? (
                                          <Loader2 className="w-3 h-3 animate-spin mr-2" />
                                        ) : (
                                          <Download className="w-3 h-3 mr-2" />
                                        )}
                                        {isCompleted
                                          ? "Unduh Kuitansi"
                                          : "Download PDF Tagihan"}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}

                      {/* --- STEP 3: CETAK NIM --- */}
                      {(() => {
                        const stepNum = currentStep++;
                        const isLocked =
                          (!isPenerimaBeasiswaPenuh && !isSudahBayar) ||
                          (isPenerimaKIPK && !isSudahKonfirmasiKIPK) ||
                          isWajibSanggahBerkas;
                        // Step ini adalah langkah final, tidak ada status isCompleted yang menggantikan UI-nya.

                        return (
                          <div
                            onClick={() => !isLocked && handleGenerateAkun()}
                            className={`group relative bg-white border rounded-xl p-4 transition-all duration-300 z-10
                ${
                  isLocked
                    ? "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 cursor-not-allowed pointer-events-none"
                    : "cursor-pointer hover:shadow-md hover:border-emerald-300 border-emerald-100 shadow-sm dark:bg-slate-950 dark:border-emerald-900/50"
                }
              `}
                          >
                            <div
                              className={`flex items-start gap-4 ${isLocked ? "opacity-60 grayscale" : ""}`}
                            >
                              <div
                                className={`relative z-10 w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ring-4 ring-white dark:ring-slate-950 transition-colors
                    ${
                      isLocked
                        ? "bg-slate-200 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                        : "bg-emerald-500 text-white shadow-md shadow-emerald-500/30"
                    }
                  `}
                              >
                                {stepNum}
                              </div>

                              <div className="flex-1 pt-0.5">
                                <div className="flex items-center justify-between mb-1">
                                  <h4 className="font-bold text-slate-900 text-sm dark:text-gray-100">
                                    {t("ukt.card.title.3")}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-500 leading-relaxed mb-3 dark:text-slate-400">
                                  {t("ukt.card.description.3")}
                                </p>

                                {!isLocked && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="w-full h-8 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/40"
                                    disabled={isPending}
                                  >
                                    {isPending ? (
                                      <Loader2 className="w-3 h-3 animate-spin mr-2" />
                                    ) : (
                                      <Printer className="w-3 h-3 mr-2" />
                                    )}
                                    Cetak NIM
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* === SECTION: SANGGAH BERKAS (VALIDATION) === */}
          {isPendingDataBerkasInvalid ? (
            <Skeleton className="h-64 w-full" />
          ) : isWajibSanggahBerkas ? (
            <div className="mt-8 space-y-6">
              {/* === VALIDATION STATUS HEADER === */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex flex-col md:flex-row gap-6 items-start dark:bg-amber-950/20 dark:border-amber-900">
                <div className="p-3 bg-amber-100 rounded-full text-amber-600 shrink-0 dark:bg-amber-900/40 dark:text-amber-500">
                  <AlertTriangle className="w-8 h-8" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-amber-900 mb-2 dark:text-amber-100">
                    Perhatian! Perbaikan Berkas Diperlukan
                  </h3>
                  <div className="text-amber-800/90 text-sm space-y-3 dark:text-amber-200/80">
                    <p>
                      Terdapat beberapa berkas yang dinyatakan{" "}
                      <strong>Tidak Valid</strong> atau memerlukan klarifikasi.
                      Silakan perbaiki berkas tersebut agar proses verifikasi
                      dapat dilanjutkan.
                    </p>
                    <div className="flex flex-wrap gap-4 mt-2">
                      <div className="flex items-center gap-2 bg-white/50 px-3 py-2 rounded-lg border border-amber-200/50 dark:bg-black/20 dark:border-amber-800">
                        <Clock className="w-4 h-4 text-amber-600 dark:text-amber-500" />
                        <span className="font-medium text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300">
                          Periode Sanggah
                        </span>
                        <span className="font-bold text-amber-900 dark:text-amber-100">
                          {dataPeriode?.waktu_mulai_sanggah_berkas_daftarkembali &&
                            formatDateIndonesia(
                              dataPeriode?.waktu_mulai_sanggah_berkas_daftarkembali,
                            )}{" "}
                          -{" "}
                          {dataPeriodeActive?.waktu_selesai_sanggah_berkas_daftarkembali &&
                            formatDateIndonesia(
                              dataPeriodeActive?.waktu_selesai_sanggah_berkas_daftarkembali,
                            )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* === FILE VALIDATION TABLE === */}
              <Card className="border-t-4 border-t-amber-500 shadow-sm overflow-hidden dark:bg-gray-950 dark:border-gray-800 dark:border-t-amber-600">
                <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4 dark:bg-gray-900/50 dark:border-gray-800">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-lg font-bold flex items-center gap-2 dark:text-gray-100">
                        <FileText className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                        Daftar Berkas Tidak Valid
                      </CardTitle>
                      <CardDescription className="dark:text-gray-400">
                        Unggah ulang dokumen yang sesuai dengan catatan
                        verifikator.
                      </CardDescription>
                    </div>
                    <Badge
                      variant="outline"
                      className="bg-white dark:bg-gray-900 dark:border-gray-700"
                    >
                      Total: {dataBerkasInvalid?.length || 0} Berkas
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table
                      id="tabel-sanggah-berkas"
                      className="w-full text-sm text-left"
                    >
                      <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b dark:bg-gray-900/50 dark:border-gray-800 dark:text-slate-400">
                        <tr>
                          <th className="px-6 py-4 font-semibold w-[50px] text-center">
                            #
                          </th>
                          <th className="px-6 py-4 font-semibold min-w-[200px]">
                            Dokumen
                          </th>
                          <th className="px-6 py-4 font-semibold min-w-[250px]">
                            Catatan Verifikator
                          </th>
                          <th className="px-6 py-4 font-semibold text-center w-[180px]">
                            Aksi
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                        {dataBerkasInvalid?.map(
                          (row: SysBerkas, index: number) => {
                            const berkasSiswa = row.data_berkas ?? null;
                            const isUploaded = berkasSiswa?.path_berkas;
                            const isUploadedSanggah =
                              berkasSiswa?.path_sanggah_berkas;
                            const isKlarifikasi =
                              berkasSiswa?.is_klarifikasi_berkas;

                            return (
                              <tr
                                key={row.id_sys_berkas}
                                className={`hover:bg-slate-50/80 transition-colors dark:hover:bg-gray-900/50 ${
                                  isUploadedSanggah
                                    ? "bg-green-50/30 dark:bg-green-900/10"
                                    : ""
                                }`}
                              >
                                {/* Status Indicator Column */}
                                <td className="px-6 py-4 text-center">
                                  {isUploadedSanggah ? (
                                    <div className="flex justify-center">
                                      <CheckCircle2 className="w-6 h-6 text-green-500 dark:text-green-400" />
                                    </div>
                                  ) : (
                                    <span className="font-mono text-slate-400 dark:text-slate-500">
                                      {index + 1}
                                    </span>
                                  )}
                                </td>

                                {/* Document Details Column */}
                                <td className="px-6 py-4 align-top">
                                  <div className="space-y-1">
                                    <p className="font-semibold text-slate-900 dark:text-slate-100">
                                      {row?.jenis_berkas?.nama_jenis_berkas}
                                    </p>
                                    <p className="text-xs text-slate-500 leading-relaxed dark:text-slate-400">
                                      {row?.jenis_berkas?.keterangan}
                                    </p>
                                    <div className="flex gap-2 mt-2">
                                      {row?.is_wajib && (
                                        <Badge
                                          variant="outline"
                                          className="text-[10px] border-red-200 text-red-600 bg-red-50 dark:bg-red-900/20 dark:border-red-900 dark:text-red-400"
                                        >
                                          Wajib
                                        </Badge>
                                      )}
                                      {row?.url_template_berkas && (
                                        <button
                                          onClick={() =>
                                            window.open(
                                              row?.url_template_berkas,
                                              "_blank",
                                            )
                                          }
                                          className="text-[10px] flex items-center gap-1 text-primary hover:underline font-medium dark:text-blue-400"
                                        >
                                          <Download className="w-3 h-3" />{" "}
                                          Contoh
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>

                                {/* Verificator Note / Warning Column */}
                                <td className="px-6 py-4 align-top">
                                  {isKlarifikasi ? (
                                    <Alert className="bg-red-50 border-red-200 py-3 dark:bg-red-900/10 dark:border-red-900">
                                      <div className="flex gap-3">
                                        <MessageCircle className="w-4 h-4 text-red-600 mt-0.5 dark:text-red-400" />
                                        <div className="space-y-1">
                                          <p className="text-red-700 font-bold text-xs dark:text-red-300">
                                            Butuh Klarifikasi
                                          </p>
                                          <p className="text-red-600 text-xs leading-snug dark:text-red-400">
                                            Silakan hubungi Helpdesk untuk
                                            instruksi lebih lanjut.
                                          </p>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100 mt-1 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/50"
                                            asChild
                                          >
                                            <a
                                              href="https://wa.me/6287730060048"
                                              target="_blank"
                                            >
                                              Hubungi WhatsApp
                                            </a>
                                          </Button>
                                        </div>
                                      </div>
                                    </Alert>
                                  ) : (
                                    <div className="bg-red-50 border border-red-100 rounded-lg p-3 dark:bg-red-900/10 dark:border-red-900/50">
                                      <p className="text-xs font-bold text-orange-400 mb-1 flex items-center gap-1 dark:text-orange-200">
                                        <AlertTriangle className="w-3 h-3" />{" "}
                                        Catatan:
                                      </p>
                                      <p className="text-sm text-orange-400 italic leading-relaxed dark:text-orange-300">
                                        "
                                        {row?.data_berkas
                                          ?.catatan_verifikator ||
                                          "Tidak ada berkas yang di unggah."}
                                        "
                                      </p>
                                    </div>
                                  )}
                                </td>

                                {/* Actions Column */}
                                <td className="px-6 py-4 align-top">
                                  {!isKlarifikasi && (
                                    <div className="flex flex-col gap-2 items-center">
                                      {/* Main Action: Upload */}
                                      {!isSelesaiWaktuSanggahBerkas && (
                                        <Button
                                          size="sm"
                                          className={`w-full ${
                                            isUploadedSanggah
                                              ? "bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 shadow-none dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                                              : "bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-700 dark:hover:bg-amber-600"
                                          }`}
                                          onClick={() => {
                                            setShowUpload(true);
                                            setSelectedBerkas(berkasSiswa);
                                            setSelectedSysBerkas(row);
                                          }}
                                        >
                                          <Upload className="w-3 h-3 mr-2" />
                                          {isUploadedSanggah
                                            ? "Ganti File"
                                            : "Upload"}
                                        </Button>
                                      )}

                                      {/* Secondary Actions: View Files */}
                                      <div className="flex w-full gap-1">
                                        {isUploaded && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="flex-1 h-8 text-xs text-slate-500 dark:text-slate-400 dark:hover:bg-gray-800"
                                            title="Lihat file lama"
                                            onClick={() => {
                                              setShowPdf(true);
                                              setSelectedBerkas(berkasSiswa);
                                            }}
                                          >
                                            <Eye className="w-3 h-3 mr-1" />{" "}
                                            Lama
                                          </Button>
                                        )}
                                        {isUploadedSanggah && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="flex-1 h-8 text-xs text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/40"
                                            title="Lihat file baru"
                                            onClick={() => {
                                              setShowPdfSanggahBerkas(true);
                                              setSelectedBerkas(berkasSiswa);
                                            }}
                                          >
                                            <Eye className="w-3 h-3 mr-1" />{" "}
                                            Baru
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}

          {/* === SECTION: DATA LOA PASCA === */}
          {isJalurPascasarjana && (
            <div className="mt-6">
              <DataLoaPasca
                dataSiswa={dataSiswa}
                idDataPendaftaran={
                  dataSiswa?.data_pendaftaran &&
                  String(dataSiswa?.data_pendaftaran[0]?.id_data_pendaftaran)
                }
              />
            </div>
          )}
        </section>
      )}

      <Modal show={showUpload} onClose={() => setShowUpload(false)}>
        <FormBerkas
          method="POST"
          selectedRow={selecedSysBerkas}
          selectedBerkas={selectedBerkas}
          closeModal={() => setShowUpload(false)}
          idDataPendaftaran={String(dataPendaftaran?.id_data_pendaftaran) || ""}
        />
      </Modal>

      <Modal
        show={showPdf}
        onClose={() => setShowPdf(false)}
        className="h-full w-[60vw]"
      >
        {selectedBerkas?.nomor ? (
          <div className="mb-3">
            <span className="py-0.5 px-3 rounded-full bg-primary font-semibold text-white">
              Nomor: {selectedBerkas?.nomor}
            </span>
          </div>
        ) : (
          ""
        )}
        <iframe
          src={`${selectedBerkas?.path_berkas}`}
          title="Berkas"
          className="absolute h-full w-full"
        />
      </Modal>

      <Modal
        show={showPdfSanggahBerkas}
        onClose={() => setShowPdfSanggahBerkas(false)}
        className="h-full w-[60vw]"
      >
        {selectedBerkas?.nomor ? (
          <div className="mb-3">
            <span className="py-0.5 px-3 rounded-full bg-primary font-semibold text-white">
              Nomor: {selectedBerkas?.nomor}
            </span>
          </div>
        ) : (
          ""
        )}
        <iframe
          src={`${selectedBerkas?.path_sanggah_berkas}`}
          title="Berkas"
          className="absolute h-full w-full"
        />
      </Modal>

      <Modal show={showAlert} onClose={() => setShowAlert(false)}>
        <div>
          <IoWarning size={50} className="mx-auto" />
          <h1 className="mt-3 mb-5 text-xl font-semibold text-center">
            Perhatian!
          </h1>
          <div className="p-3 mt-3 rounded-xl border border-yellow-500 bg-yellow-50">
            <h1>Mohon maaf, saat ini anda tidak bisa melakukan cetak nim.</h1>
            <br />
            <p className="text-primary">{error?.response?.data?.message}</p>
            <br />
          </div>
          <div className="mt-5">
            <Button variant={"outline"} onClick={() => setShowAlert(false)}>
              Tutup
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        show={showAlertKuitansi}
        onClose={() => setShowAlertKuitansi(false)}
      >
        <div>
          <IoWarning size={50} className="mx-auto" />
          <h1 className="mt-3 mb-5 text-xl font-semibold text-center">
            Perhatian!
          </h1>
          <div className="p-3 mt-3 rounded-xl border border-yellow-500 bg-yellow-50">
            <h1>
              Mohon maaf, saat ini anda belum bisa melakukan cetak kuitansi.
            </h1>
            <br />
            <p className="text-primary">
              {errorGenerateKuitansi?.response?.data?.message}
            </p>
            <br />
          </div>
          <div className="mt-5">
            <Button
              variant={"outline"}
              onClick={() => setShowAlertKuitansi(false)}
            >
              Tutup
            </Button>
          </div>
        </div>
      </Modal>

      <Dialog open={showGenerateTagihan} onOpenChange={setShowGenerateTagihan}>
        <DialogContent className="max-w-lg sm:max-w-xl max-h-[90vh] overflow-y-auto dark:bg-gray-950 dark:border-gray-800">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-primary/10 rounded-full text-primary dark:bg-primary/20 dark:text-primary-400">
                <Banknote size={24} />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold dark:text-gray-100">
                  {t("invoice.title")} UKT
                </DialogTitle>
                <DialogDescription className="dark:text-gray-400">
                  {t("invoice.description")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* CEK APAKAH ADA TAGIHAN AKTIF */}
          {isPendingDataTagihanUkt ? (
            /* === LOADING SKELETON === */
            <div className="space-y-6 py-2 animate-pulse">
              {/* === SECTION 1: INVOICE SUMMARY SKELETON === */}
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-5 space-y-4 dark:bg-gray-900/50 dark:border-gray-800">
                {/* Header Rincian */}
                <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-gray-700">
                  <div className="h-5 w-32 bg-slate-200 rounded-md dark:bg-gray-800" />
                  <div className="h-5 w-20 bg-slate-200 rounded-full dark:bg-gray-800" />
                </div>

                {/* Detail Info Siswa */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <div className="h-4 w-28 bg-slate-200 rounded-md dark:bg-gray-800" />
                    <div className="h-4 w-36 bg-slate-200 rounded-md dark:bg-gray-800" />
                  </div>
                  <div className="flex justify-between">
                    <div className="h-4 w-24 bg-slate-200 rounded-md dark:bg-gray-800" />
                    <div className="h-4 w-40 bg-slate-200 rounded-md dark:bg-gray-800" />
                  </div>
                </div>

                <Separator className="bg-slate-200 dark:bg-gray-700" />

                {/* Cost Breakdown */}
                <div className="space-y-3 pt-2">
                  <div className="flex justify-between">
                    <div className="h-4 w-24 bg-slate-200 rounded-md dark:bg-gray-800" />
                    <div className="h-4 w-28 bg-slate-200 rounded-md dark:bg-gray-800" />
                  </div>
                  <div className="flex justify-between">
                    <div className="h-4 w-20 bg-slate-200 rounded-md dark:bg-gray-800" />
                    <div className="h-4 w-24 bg-slate-200 rounded-md dark:bg-gray-800" />
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-dashed border-slate-300 dark:border-gray-700">
                    <div className="h-5 w-24 bg-slate-300 rounded-md dark:bg-gray-700" />
                    <div className="h-6 w-32 bg-slate-300 rounded-md dark:bg-gray-700" />
                  </div>
                </div>
              </div>

              {/* === SECTION 3: PAYMENT METHOD SKELETON === */}
              <div className="space-y-3">
                <div className="h-5 w-40 bg-slate-200 rounded-md dark:bg-gray-800 mb-2" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-16 bg-slate-100 rounded-xl border border-slate-200 dark:bg-gray-800/50 dark:border-gray-700 flex items-center p-3 gap-3"
                    >
                      <div className="w-10 h-10 bg-slate-200 rounded-lg dark:bg-gray-700 shrink-0" />
                      <div className="h-4 w-24 bg-slate-200 rounded-md dark:bg-gray-700" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer Buttons Skeleton (Opsional, karena DialogFooter sudah ada di luar blok ini) */}
              {/* <div className="flex justify-end gap-2 mt-4 pt-4 border-t dark:border-gray-800">
                <div className="h-10 w-24 bg-slate-200 rounded-md dark:bg-gray-800" />
                <div className="h-10 w-32 bg-slate-300 rounded-md dark:bg-gray-700" />
              </div> */}
            </div>
          ) : !BISA_GENERATE_TAGIHAN ? (
            <div className="py-6">
              <Alert
                variant="destructive"
                className="border-amber-500/50 bg-amber-50 text-amber-900 dark:bg-amber-950/20 dark:border-amber-900/50 dark:text-amber-300"
              >
                <AlertTriangle className="h-5 w-5" />
                <AlertTitle className="font-bold">
                  Tagihan Masih Aktif
                </AlertTitle>
                <AlertDescription className="mt-2 text-sm leading-relaxed">
                  Anda masih memiliki tagihan UKT yang belum kadaluarsa. Silakan
                  selesaikan pembayaran menggunakan kode tagihan sebelumnya,
                  atau tunggu hingga tagihan tersebut kedaluwarsa untuk dapat
                  membuat tagihan baru dengan metode pembayaran yang berbeda.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            /* JIKA TIDAK ADA TAGIHAN AKTIF (ATAU SUDAH EXPIRED), TAMPILKAN FORM NORMAL */
            <div className="space-y-6 py-2">
              {!dataSiswa?.ukuran_baju && (
                <div className="space-y-4">
                  {" "}
                  {/* Tambah spacing antar elemen */}
                  {/* --- UI UX Tambahan: Info Melengkapi Data --- */}
                  <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-xl">
                    <div className="mt-0.5 text-amber-600 dark:text-amber-400">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                        Biodata Belum Lengkap
                      </h4>
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 leading-relaxed">
                        Kami mendeteksi Anda belum mengisi informasi ukuran
                        baju. Silakan lengkapi data di bawah ini untuk
                        melanjutkan proses administrasi.
                      </p>
                    </div>
                  </div>
                  {/* ------------------------------------------ */}
                  <div className="space-y-2">
                    <Label
                      required
                      className="flex items-center justify-between"
                    >
                      Ukuran Jas/Baju
                    </Label>
                    <Combobox
                      open={openSize}
                      setOpen={setOpenSize}
                      value={form.ukuran_baju || ""}
                      setValue={(v) => setForm({ ...form, ukuran_baju: v })}
                      options={listSizeOptions}
                      placeholder="Pilih Ukuran"
                    />
                  </div>
                  {tampilkanFormLokasi && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                      <div className="space-y-2">
                        <Label required>Minat Lokasi Kuliah</Label>
                        <Select
                          value={form.pilihan_lokasi_kuliah || ""}
                          onValueChange={(v) => {
                            if (!v) return;
                            setForm({
                              ...form,
                              pilihan_lokasi_kuliah: v as LokasiKuliah,
                            });
                          }}
                        >
                          <SelectTrigger className="bg-white dark:bg-slate-900">
                            <SelectValue placeholder="Pilih Lokasi" />
                          </SelectTrigger>
                          <SelectContent>
                            {pilihanKelas.map((v, i) => (
                              <SelectItem key={i} value={v.toLowerCase()}>
                                {v.charAt(0).toUpperCase() +
                                  v.slice(1).toLowerCase()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                        <strong>Catatan:</strong> Lokasi final ditentukan
                        berdasarkan kapasitas kelas dan domisili KTP. Pilihan
                        ini hanya sebagai preferensi.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* === SECTION 1: INVOICE SUMMARY === */}
              <div className="bg-slate-50 border rounded-xl p-5 space-y-4 dark:bg-gray-900/50 dark:border-gray-800">
                <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-gray-700">
                  <h3 className="font-bold text-slate-700 flex items-center gap-2 dark:text-gray-200">
                    <FileText className="w-4 h-4" />
                    {t("invoice.title.paid")}
                  </h3>
                  <Badge
                    variant="outline"
                    className="bg-white dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700"
                  >
                    {t("ukt.payment.status-1")}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground dark:text-gray-400">
                      {t("dataPendaftaran.nomor-pendaftaran")}
                    </span>
                    <span className="font-medium text-slate-900 font-mono dark:text-gray-100">
                      {dataPendaftaran?.nomor_pendaftaran}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground dark:text-gray-400">
                      {t("dataSiswa.nama-lengkap")}
                    </span>
                    <span className="font-medium text-slate-900 text-right dark:text-gray-100">
                      {dataSiswa?.nama_lengkap}
                    </span>
                  </div>
                </div>

                <Separator className="bg-slate-200 dark:bg-gray-700" />

                {/* Cost Breakdown */}
                <div className="space-y-2 text-sm">
                  {isJalurMandiri ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground dark:text-gray-400">
                          {t("invoice.text.biaya")} (UKT)
                        </span>
                        <span className="font-medium dark:text-gray-200">
                          {formatRupiah(
                            Number(
                              dataPendaftaran?.data_penetapan_ukt?.nominal_ukt,
                            ),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground dark:text-gray-400">
                          {t("invoice.text.biayaIpi")}
                        </span>
                        <span className="font-medium dark:text-gray-200">
                          {formatRupiah(
                            Number(
                              dataPendaftaran?.data_penetapan_ukt?.nominal_ipi,
                            ),
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-dashed border-slate-300 dark:border-gray-700">
                        <span className="font-bold text-slate-900 dark:text-gray-100">
                          {t("invoice.text.biayaTotal")}
                        </span>
                        <span className="font-bold text-primary text-lg dark:text-primary-400">
                          {formatRupiah(
                            Number(
                              dataPendaftaran?.data_penetapan_ukt
                                ?.total_tagihan,
                            ),
                          )}
                        </span>
                      </div>
                    </>
                  ) : isJalurPascasarjana &&
                    dataPendaftaran?.data_penetapan_ukt?.is_cicil_bayar ? (
                    <>
                      {/* <div className="flex justify-between items-center">
                        <span className="font-bold text-slate-700 dark:text-gray-300">
                          Tagihan cicilan Tahap 1
                        </span>
                        <span className="font-bold text-primary text-xl dark:text-primary-400">
                          {formatRupiah(
                            Number(
                              dataPendaftaran?.data_penetapan_ukt
                                ?.total_tagihan,
                            ),
                          )}
                        </span>
                      </div> */}
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-slate-700 dark:text-gray-300">
                        {t("invoice.text.biaya")} Total
                      </span>
                      <span className="font-bold text-primary text-xl dark:text-primary-400">
                        {formatRupiah(
                          Number(
                            dataPendaftaran?.data_penetapan_ukt?.total_tagihan,
                          ),
                        )}
                      </span>
                    </div>
                  )}

                  {dataPendaftaran?.data_penetapan_ukt?.is_cicil_bayar ? (
                    <div className="flex justify-between items-center mt-2 pt-3 border-t border-dashed border-slate-300 dark:border-gray-700">
                      <span className="font-bold text-slate-700 dark:text-gray-300">
                        Nominal Cicilan Awal
                      </span>
                      <span className="font-bold text-primary text-xl dark:text-primary-400">
                        {formatRupiah(
                          Number(
                            dataPendaftaran?.data_penetapan_ukt
                              ?.nominal_cicilan_awal,
                          ),
                        )}
                      </span>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>

              {/* === SECTION 2: FILE UPLOAD (Conditional) === */}
              {!isJalurPascasarjana &&
                !isJalurProfesi &&
                !isJalurInternasional && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <Label className="text-base font-semibold dark:text-gray-200">
                        Surat Pernyataan Kesanggupan
                      </Label>
                      <a
                        href={dataPeriode?.url_surat_pernyataan_non_refund_ukt}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-primary font-medium hover:underline flex items-center gap-1 dark:text-primary-400"
                      >
                        <Button variant={"outline"}>
                          <FileText className="w-3 h-3 mr-3" /> Unduh Format
                        </Button>
                      </a>
                    </div>

                    <div
                      className={`border-2 border-dashed rounded-xl p-6 transition-colors text-center group ${
                        fileSuratPernyataanUKT
                          ? "border-primary/50 bg-primary/5 dark:bg-primary/10 dark:border-primary/50"
                          : "border-slate-200 hover:bg-slate-50 dark:border-gray-700 dark:hover:bg-gray-900/50"
                      }`}
                    >
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        accept=".pdf, image/*"
                        onChange={handleFileChange}
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer block w-full h-full"
                      >
                        {fileSuratPernyataanUKT ? (
                          /* --- TAMPILAN JIKA FILE SUDAH DIPILIH --- */
                          <div className="flex flex-col items-center justify-center py-2">
                            <div className="w-12 h-12 bg-primary/20 text-primary rounded-full flex items-center justify-center mb-3">
                              <FileText size={24} />
                            </div>
                            <p className="text-sm font-semibold text-slate-800 dark:text-gray-200 max-w-[250px] truncate px-4">
                              {fileSuratPernyataanUKT &&
                                fileSuratPernyataanUKT.name}
                            </p>
                            <p className="text-xs text-primary mt-2 font-medium">
                              Klik untuk mengganti file
                            </p>
                          </div>
                        ) : (
                          /* --- TAMPILAN JIKA BELUM ADA FILE (DEFAULT) --- */
                          <>
                            <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform dark:bg-primary/20 dark:text-primary-400">
                              <UploadCloud size={20} />
                            </div>
                            <p className="text-sm font-medium text-slate-700 mb-1 dark:text-gray-300">
                              Klik untuk upload
                            </p>
                            <p className="text-xs text-muted-foreground mb-3 dark:text-gray-500">
                              Format PDF, JPG, PNG (Max 1MB)
                            </p>

                            <div className="flex gap-1 justify-center flex-wrap mt-2">
                              <Badge
                                variant="outline"
                                className="text-[10px] h-5 dark:border-gray-600 dark:text-gray-400"
                              >
                                PDF
                              </Badge>
                              <Badge
                                variant="outline"
                                className="text-[10px] h-5 dark:border-gray-600 dark:text-gray-400"
                              >
                                JPG
                              </Badge>
                              <Badge
                                variant="outline"
                                className="text-[10px] h-5 dark:border-gray-600 dark:text-gray-400"
                              >
                                PNG
                              </Badge>
                            </div>
                          </>
                        )}
                      </label>
                    </div>

                    {errorGenerateTagihan?.response?.data?.errors
                      ?.file_surat_pernyataan_ukt && (
                      <div className="text-destructive text-sm flex items-center gap-2 mt-1 bg-destructive/10 p-2 rounded-md dark:bg-red-900/20 dark:text-red-400">
                        <AlertCircle className="w-4 h-4" />
                        {errorGenerateTagihan.response.data.errors.file_surat_pernyataan_ukt.join(
                          ", ",
                        )}
                      </div>
                    )}
                  </div>
                )}

              {/* === SECTION 3: PAYMENT METHOD === */}
              <div className="space-y-3">
                <h3 className="font-semibold text-base uppercase tracking-tight text-slate-900 dark:text-gray-100">
                  {t("invoice.text.method")}
                </h3>

                {isPendingListPaymentMethodPlaza ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="h-16 bg-slate-100 rounded-xl animate-pulse dark:bg-gray-800"
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {listPaymentMethodPlaza?.map(
                      (paymentMethod: PaymentMethod) => {
                        const isSelected =
                          selectedPaymentMethod?.id === paymentMethod.id;
                        return (
                          <div
                            key={paymentMethod.id}
                            onClick={() =>
                              setSelectedPaymentMethod(paymentMethod)
                            }
                            className={`
                                      relative flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all
                                      ${
                                        isSelected
                                          ? "border-primary bg-primary/5 shadow-sm dark:border-primary-500 dark:bg-primary-900/20"
                                          : "border-slate-100 hover:border-slate-300 hover:bg-slate-50 dark:border-gray-800 dark:hover:border-gray-600 dark:hover:bg-gray-800"
                                      }
                                  `}
                          >
                            <div className="w-10 h-10 bg-white rounded-lg border p-1 flex items-center justify-center flex-shrink-0 dark:bg-gray-200 dark:border-gray-300">
                              <img
                                src={paymentMethod.logo}
                                alt={paymentMethod.name}
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                            <span
                              className={`font-semibold text-sm ${isSelected ? "text-primary dark:text-primary-400" : "text-slate-700 dark:text-gray-300"}`}
                            >
                              {paymentMethod.name}
                            </span>
                            {isSelected && (
                              <div className="absolute top-2 right-2 text-primary dark:text-primary-400">
                                <CheckCircle2 className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowGenerateTagihan(false)}
              disabled={isPendingGenerateTagihan}
              className="dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t("siswa.layout.button.close")}
            </Button>

            {/* SEMBUNYIKAN TOMBOL JIKA TAGIHAN AKTIF */}
            {BISA_GENERATE_TAGIHAN && (
              <Button
                onClick={handleGenerateTagihanUkt}
                disabled={isPendingGenerateTagihan || !selectedPaymentMethod}
                className="w-full sm:w-auto min-w-[140px]"
              >
                {isPendingGenerateTagihan ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>{t("ukt.button.unpaid.invoice")}</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showTagihan} onOpenChange={setShowTagihan}>
        <DialogContent className="lg:max-w-2xl max-h-[80vh] overflow-auto sm:rounded-2xl dark:bg-gray-950 dark:border-gray-800">
          <DialogHeader className="pb-4 border-b dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div
                className={`p-3 rounded-full ${isSudahBayar ? "bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400" : "bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"}`}
              >
                {isSudahBayar ? (
                  <CheckCircle2 size={24} />
                ) : (
                  <Wallet size={24} />
                )}
              </div>
              <div className="text-left">
                <DialogTitle className="text-xl font-bold dark:text-gray-100">
                  {isSudahBayar
                    ? t("invoice.title.paid")
                    : t("invoice.title.unpaid")}
                </DialogTitle>
                <DialogDescription className="mt-1 text-sm dark:text-gray-400">
                  {isSudahBayar
                    ? t("invoice.description.paid")
                    : t("invoice.description.unpaid")}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            {listTagihanUkt?.map(
              (tagihan: DataTagihanUktPlaza, index: number) => {
                const paymentMethod = listPaymentMethodPlaza?.find(
                  (item: PaymentMethod) =>
                    item.id == tagihan.payment_gateway_id,
                );

                // Determine if we should default open the first unpaid bill
                const defaultValue =
                  !isSudahBayar && index === 0
                    ? `item-${tagihan.id_data_tagihan_ukt_plaza}`
                    : "";

                return (
                  <Accordion
                    key={tagihan.id_data_tagihan_ukt_plaza}
                    type="single"
                    collapsible
                    defaultValue={defaultValue}
                    className="border rounded-xl shadow-sm bg-white overflow-hidden dark:bg-gray-900 dark:border-gray-800"
                  >
                    <AccordionItem
                      value={`item-${tagihan.id_data_tagihan_ukt_plaza}`}
                      className="border-0"
                    >
                      <AccordionTrigger className="group px-3 sm:px-5 py-3 sm:py-4 hover:bg-slate-50 transition-all [&[data-state=open]]:bg-slate-50/50 dark:hover:bg-gray-800 dark:[&[data-state=open]]:bg-gray-800/50 hover:no-underline">
                        <div className="flex items-start sm:items-center gap-3 sm:gap-4 w-full text-left pr-2 sm:pr-0">
                          {/* Bank Logo / Icon */}
                          {!isAfirmasi && (
                            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-white border rounded-lg p-1 sm:p-1.5 flex items-center justify-center dark:bg-gray-800 dark:border-gray-700">
                              {paymentMethod?.logo ? (
                                <img
                                  src={paymentMethod.logo}
                                  alt={paymentMethod.name}
                                  className="max-w-full max-h-full object-contain"
                                />
                              ) : (
                                <Banknote className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 dark:text-slate-500" />
                              )}
                            </div>
                          )}

                          {/* Main Header Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                              <h3 className="font-bold text-sm sm:text-base text-slate-900 truncate dark:text-gray-100">
                                {paymentMethod?.name || tagihan?.nama_tagihan}
                              </h3>
                              <div className="w-fit">
                                <StatusBadge
                                  status={tagihan?.status_bayar}
                                  isAfirmasi={isAfirmasi}
                                  isPenerimaKIPK={isPenerimaKIPK}
                                />
                              </div>
                            </div>
                            <p className="text-[11px] sm:text-sm text-muted-foreground truncate dark:text-gray-400">
                              {tagihan?.nama_tagihan}
                            </p>

                            {/* PERBAIKAN MOBILE: Tambahkan group-data-[state=open]:hidden */}
                            <div className="sm:hidden group-data-[state=open]:hidden mt-1.5 font-bold text-primary dark:text-blue-400 text-xs transition-all">
                              {formatRupiah(Number(tagihan?.amount))}
                            </div>
                          </div>

                          {/* PERBAIKAN DESKTOP: Gunakan sm:group-data-[state=open]:hidden agar menang melawan sm:block */}
                          <div className="hidden sm:block sm:group-data-[state=open]:hidden text-right mr-2 shrink-0 transition-all">
                            <div className="font-bold text-primary dark:text-blue-400">
                              {formatRupiah(Number(tagihan?.amount))}
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>

                      <AccordionContent className="px-0 pb-0">
                        <div className="px-5 pb-6 pt-2">
                          <Separator className="mb-5 dark:bg-gray-800" />

                          {/* Payment Details Grid */}
                          <div
                            className={`grid grid-cols-1 ${isSudahBayar ? "md:grid-cols-1" : "md:grid-cols-2"}  gap-6`}
                          >
                            {/* LEFT: Financial Details */}
                            <div className="space-y-4">
                              <div>
                                <label className="text-xs font-semibold uppercase text-muted-foreground mb-1 block dark:text-gray-400">
                                  Nominal Tagihan
                                </label>
                                <div className="text-2xl font-bold text-primary dark:text-blue-400">
                                  {formatRupiah(Number(tagihan?.amount))}
                                </div>
                              </div>

                              <div>
                                <label className="text-xs font-semibold uppercase text-muted-foreground mb-2 block dark:text-gray-400">
                                  {t("invoice.label.kode-pembayaran")} (VA/Kode)
                                </label>
                                <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg border border-slate-200 dark:bg-gray-800 dark:border-gray-700">
                                  <code className="text-lg font-mono font-semibold tracking-wide flex-1 text-slate-900 dark:text-gray-100">
                                    {tagihan?.nomor_tagihan}
                                  </code>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 hover:bg-white hover:text-primary hover:shadow-sm dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white"
                                    onClick={() => {
                                      navigator.clipboard.writeText(
                                        `${tagihan?.nomor_tagihan}`,
                                      );
                                      toast.success("Kode pembayaran disalin!");
                                      setIsCopied(true); // You might need a state per item or global
                                      setTimeout(
                                        () => setIsCopied(false),
                                        2000,
                                      );
                                    }}
                                  >
                                    {isCopied ? (
                                      <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </Button>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs font-semibold uppercase text-muted-foreground mb-1 block dark:text-gray-400">
                                    Berlaku Hingga
                                  </label>
                                  <p className="font-medium text-sm flex items-center gap-2 dark:text-gray-200">
                                    <Clock className="w-3.5 h-3.5 text-amber-500" />
                                    {tagihan?.expired_date
                                      ? formatDate(
                                          tagihan.expired_date,
                                          "full",
                                          "id",
                                        )
                                      : "-"}
                                  </p>
                                </div>
                                <div>
                                  <label className="text-xs font-semibold uppercase text-muted-foreground mb-1 block dark:text-gray-400">
                                    Keterangan
                                  </label>
                                  <p className="font-medium text-sm text-slate-700 dark:text-gray-300">
                                    {tagihan?.keterangan || "-"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* RIGHT: Payment Instructions (Conditionals) */}
                            {!isSudahBayar && (
                              <div className="bg-blue-50/50 rounded-xl p-5 border border-blue-100 dark:bg-blue-900/10 dark:border-blue-900/30">
                                <h4 className="font-bold text-blue-800 flex items-center gap-2 mb-3 dark:text-blue-300">
                                  <Info className="w-4 h-4" /> Cara Pembayaran
                                </h4>

                                <div className="text-sm text-slate-600 space-y-3 dark:text-slate-300">
                                  {/* Specific Logic for BRI (ID 1) */}
                                  {tagihan?.payment_gateway_id == 1 && (
                                    <>
                                      <p>{t("invoice.instruction.bri.1")}</p>
                                      <Button
                                        asChild
                                        variant="outline"
                                        size="sm"
                                        className="w-full bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-blue-900 dark:hover:bg-gray-700"
                                      >
                                        <a
                                          href={web.linkProsedurPembayaranBRI}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {t("invoice.instruction.bri.3")}{" "}
                                          <ExternalLink className="w-3 h-3 ml-2" />
                                        </a>
                                      </Button>
                                    </>
                                  )}

                                  {/* Specific Logic for BPD (ID 3) */}
                                  {tagihan?.payment_gateway_id == 3 && (
                                    <>
                                      <ul className="list-decimal list-outside pl-4 space-y-1">
                                        <li>
                                          {t("invoice.instruction.bpd.1")}
                                        </li>
                                        <li>
                                          {t("invoice.instruction.bpd.2")}
                                        </li>
                                        <li>
                                          {t("invoice.instruction.bpd.3")}
                                        </li>
                                      </ul>
                                      <div className="mt-2 pt-2 border-t border-blue-100 dark:border-blue-900/30">
                                        <p className="text-xs mb-2">
                                          {t("invoice.instruction.bpd.4")}
                                        </p>
                                        <Button
                                          asChild
                                          variant="outline"
                                          size="sm"
                                          className="w-full bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-blue-900 dark:hover:bg-gray-700"
                                        >
                                          <a
                                            href={web.linkProsedurPembayaranBPD}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            {t("invoice.instruction.bpd.5")}{" "}
                                            <ExternalLink className="w-3 h-3 ml-2" />
                                          </a>
                                        </Button>
                                      </div>
                                    </>
                                  )}

                                  {/* Specific Logic for BNI (ID 5) */}
                                  {tagihan?.payment_gateway_id == 5 && (
                                    <>
                                      <p>{t("invoice.instruction.bni.1")}</p>
                                      <Button
                                        asChild
                                        variant="outline"
                                        size="sm"
                                        className="w-full bg-white text-blue-600 border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-blue-900 dark:hover:bg-gray-700"
                                      >
                                        <a
                                          href={web.linkProsedurPembayaranBNI}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {t("invoice.instruction.bni.3")}{" "}
                                          <ExternalLink className="w-3 h-3 ml-2" />
                                        </a>
                                      </Button>
                                    </>
                                  )}

                                  {/* Fallback if no ID matches but instruction text exists */}
                                  {![1, 3, 5].includes(
                                    tagihan?.payment_gateway_id,
                                  ) && (
                                    <p className="italic text-xs text-slate-500 dark:text-slate-400">
                                      Silahkan lakukan pembayaran melalui teller
                                      bank, ATM, atau mobile banking sesuai
                                      dengan bank tujuan.
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                );
              },
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirmKIPK} onOpenChange={setShowConfirmKIPK}>
        {/* Menggunakan max-w-md agar fokus dan tidak terlalu lebar */}
        <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0 dark:bg-slate-950 dark:border-slate-800">
          {isPendingDataBerkasInvalid ? (
            <div className="p-6 space-y-4">
              <Skeleton className="h-12 w-12 rounded-full mx-auto" />
              <Skeleton className="h-6 w-3/4 mx-auto" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : isWajibSanggahBerkas ? (
            /* STATE 1: DITANGGUHKAN / INVALID */
            <>
              <div className="bg-amber-50 p-6 flex flex-col items-center text-center border-b border-amber-100 dark:bg-amber-950/20 dark:border-amber-900">
                <div className="h-16 w-16 bg-amber-100 rounded-full flex items-center justify-center mb-4 dark:bg-amber-900/40">
                  <FileWarning className="h-8 w-8 text-amber-600 dark:text-amber-500" />
                </div>
                <DialogTitle className="text-xl font-bold text-amber-900 dark:text-amber-100">
                  Status Ditangguhkan
                </DialogTitle>
                <DialogDescription className="text-amber-800/80 text-sm leading-relaxed dark:text-amber-400">
                  Mohon maaf, kami menemukan beberapa berkas penunjang yang{" "}
                  <strong>tidak valid</strong>. Anda belum dapat melakukan
                  konfirmasi KIP-K saat ini.
                </DialogDescription>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-slate-50 p-3 rounded-lg border text-sm text-slate-600 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400">
                  <p className="font-semibold text-slate-900 mb-1 dark:text-slate-100">
                    Apa yang harus dilakukan?
                  </p>
                  Silakan tutup jendela ini, lalu periksa{" "}
                  <strong>Tabel Sanggah Berkas</strong> di bawah untuk melihat
                  detail kesalahan dan mengunggah ulang dokumen.
                </div>

                <DialogFooter className="sm:justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmKIPK(false)}
                    className="w-full dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-900"
                  >
                    Tutup & Perbaiki Berkas
                  </Button>
                </DialogFooter>
              </div>
            </>
          ) : (
            /* STATE 2: SUKSES / KONFIRMASI */
            <>
              <div className="bg-gradient-to-b from-green-50 to-white p-8 flex flex-col items-center text-center dark:from-green-950/20 dark:to-slate-950">
                <div className="relative mb-4">
                  <div className="absolute inset-0 bg-green-200 blur-xl opacity-50 rounded-full dark:bg-green-900/30"></div>
                  <div className="relative h-20 w-20 bg-green-100 rounded-full flex items-center justify-center border-4 border-white shadow-sm dark:bg-green-900 dark:border-slate-900 dark:shadow-none">
                    <Award className="h-10 w-10 text-green-600 dark:text-green-400" />
                  </div>
                </div>

                <DialogTitle className="text-2xl font-bold text-slate-900 mb-2 dark:text-slate-100">
                  Selamat! Anda Penerima KIP-K
                </DialogTitle>

                <DialogDescription className="text-slate-600 max-w-xs mx-auto dark:text-slate-400">
                  Biaya UKT Anda{" "}
                  <span className="font-bold text-green-600 dark:text-green-400">
                    sepenuhnya ditanggung
                  </span>
                  . Langkah terakhir adalah melakukan konfirmasi di bawah ini.
                </DialogDescription>
              </div>

              <div className="px-6 pb-6 pt-2">
                <div className="flex flex-col gap-3">
                  <Button
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-700 shadow-md shadow-green-200 dark:shadow-none dark:bg-green-700 dark:hover:bg-green-600"
                    onClick={handleKonfirmasiKIPK}
                    disabled={isPendingKonfirmasiKIPK}
                  >
                    {isPendingKonfirmasiKIPK ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Memproses...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" /> Konfirmasi Penerima
                        KIP-K
                      </>
                    )}
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => setShowConfirmKIPK(false)}
                    disabled={isPendingKonfirmasiKIPK}
                    className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
                  >
                    Batal
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Modal
        show={showSuratPernyataanUKT}
        onClose={() => setShowSuratPernyataanUKT(false)}
        className="h-full lg:w-[60vw] w-[95vw]"
      >
        <iframe
          src={`${dataPendaftaran?.path_surat_pernyataan_ukt}`}
          title="Berkas"
          className="absolute h-full w-full"
        />
      </Modal>

      {/* <Modal
        show={showUploadUlangSurat}
        onClose={() => setShowUploadUlangSurat(false)}
      >
        <div>
          <FormHeader
            title="UPLOAD SURAT PERNYATAAN KESIAPAN TIDAK MENGEMBALIKAN UKT"
            description=""
            icon={<LuUpload />}
          />

          <div>
            <Label>Surat Pernyataan</Label>
            <p>
              <Link
                to={web.linkSuratPernyataanTidakMengembalikanUkt}
                target="_blank"
                className="text-primary font-semibold underline"
              >
                Unduh Format
              </Link>
            </p>
            <Input
              type="file"
              accept=".pdf, image/*"
              onChange={handleFileChange}
            />
            <div>
              <Badge variant={"dark"}>.pdf</Badge>
              <Badge variant={"dark"}>.png</Badge>
              <Badge variant={"dark"}>.jpg</Badge>
              <Badge variant={"dark"}>.jpeg</Badge>
              <Badge variant={"default"}>1 MB</Badge>
            </div>
            <InputError
              text={errorUploadUlangSurat?.response?.data?.errors?.file_surat_pernyataan_ukt?.join(
                ",",
              )}
            />
          </div>
          <div className="mt-10 flex gap-3">
            <Button
              variant={"outline"}
              onClick={() => setShowUploadUlangSurat(false)}
              disabled={isPendingUploadUlangSurat}
            >
              Batal
            </Button>
            <Button
              onClick={handleUploadUlangSuratPernyataanUkt}
              disabled={isPendingUploadUlangSurat}
            >
              {isPendingUploadUlangSurat ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Upload Ulang"
              )}
            </Button>
          </div>
        </div>
      </Modal> */}

      <Dialog
        open={showUploadUlangSurat}
        onOpenChange={setShowUploadUlangSurat}
      >
        <DialogContent className="sm:max-w-md dark:bg-slate-950 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
              <UploadCloud className="w-5 h-5 text-primary" />
              Upload Surat Pernyataan
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Unggah surat pernyataan kesiapan tidak mengembalikan UKT yang
              telah ditandatangani.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Section: Download Template */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-center justify-between dark:bg-blue-900/20 dark:border-blue-900/50">
              <div className="flex items-center gap-3">
                <div className="bg-white p-2 rounded-md border border-blue-100 shadow-sm dark:bg-slate-800 dark:border-slate-700">
                  <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-blue-900 dark:text-blue-300">
                    Format Surat
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-400/80">
                    Wajib menggunakan format ini
                  </p>
                </div>
              </div>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-blue-700 hover:text-blue-800 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/40 dark:hover:text-blue-300"
              >
                <Link
                  to={dataPeriode?.url_surat_pernyataan_non_refund_ukt || ""}
                  target="_blank"
                >
                  <Download className="w-4 h-4 mr-2" /> Unduh
                </Link>
              </Button>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="upload-surat"
                className={`group relative flex flex-col items-center justify-center w-full h-40 rounded-xl border-2 border-dashed transition-all cursor-pointer ${
                  // Ubah warna background dan border jika file sudah terisi
                  fileSuratPernyataanUKT
                    ? "border-primary/50 bg-primary/5 dark:bg-primary/10 dark:border-primary/50"
                    : "border-slate-300 hover:border-primary/50 hover:bg-slate-50 bg-slate-50/30 dark:border-slate-800 dark:bg-slate-900/20 dark:hover:bg-slate-900/40 dark:hover:border-primary/50"
                }`}
              >
                {/* Hidden Input */}
                <input
                  id="upload-surat"
                  type="file"
                  className="hidden"
                  accept=".pdf, image/png, image/jpeg, image/jpg"
                  onChange={handleFileChange}
                />

                {/* Visual Content inside Label */}
                {fileSuratPernyataanUKT ? (
                  /* --- TAMPILAN JIKA FILE SUDAH DIPILIH --- */
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center px-4 w-full">
                    <div className="mb-3 p-3 rounded-full bg-primary/20 text-primary dark:bg-primary/30 dark:text-primary-400">
                      <FileText className="w-8 h-8" />
                    </div>
                    {/* max-w-[80%] dan truncate akan memotong teks yang panjang dengan titik-titik (...) */}
                    <p className="mb-1 text-sm font-semibold text-slate-800 dark:text-slate-200 max-w-[80%] truncate">
                      {fileSuratPernyataanUKT && fileSuratPernyataanUKT.name}
                    </p>
                    <p className="text-xs text-primary font-medium mt-1">
                      Klik untuk mengganti file
                    </p>
                  </div>
                ) : (
                  /* --- TAMPILAN DEFAULT (KOSONG) --- */
                  <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center px-4">
                    <div className="mb-3 p-3 rounded-full bg-slate-100 group-hover:bg-white group-hover:shadow-md transition-all dark:bg-slate-800 dark:group-hover:bg-slate-700">
                      <UploadCloud className="w-6 h-6 text-slate-400 group-hover:text-primary dark:text-slate-500" />
                    </div>
                    <p className="mb-1 text-sm font-medium text-slate-700 dark:text-slate-300">
                      <span className="font-semibold text-primary">
                        Klik untuk upload
                      </span>{" "}
                      {/* atau drag & drop */}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-500">
                      PDF, JPG, PNG (Maks. {selecedSysBerkas?.max_upload})
                    </p>
                  </div>
                )}
              </label>

              {/* Error Message */}
              {errorUploadUlangSurat?.response?.data?.errors
                ?.file_surat_pernyataan_ukt && (
                <div className="flex items-start gap-2 p-2 bg-red-50 text-red-700 rounded-md text-xs border border-red-100 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/50">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {errorUploadUlangSurat.response.data.errors.file_surat_pernyataan_ukt.join(
                      ", ",
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowUploadUlangSurat(false)}
              disabled={isPendingUploadUlangSurat}
              className="dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-900"
            >
              Batal
            </Button>
            <Button
              onClick={handleUploadUlangSuratPernyataanUkt}
              disabled={isPendingUploadUlangSurat}
              className="min-w-[120px] dark:bg-primary dark:text-primary-foreground"
            >
              {isPendingUploadUlangSurat ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Proses...
                </>
              ) : (
                "Upload"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
